/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/ManagerAttendancePage.tsx
 * PURPOSE: SC-011-ATTEND-MGR "Chấm công" (Quản lý) — REDESIGN 2026-08-17 per
 *          BS-400 "hệ thống hiểu ca/khớp ca" (auto shift-match). Replaces the
 *          old manual approve/flag/OT-popover workflow with the auto-match
 *          table (Nhân viên · Ca · Thực tế · Được tính · Địa điểm · Trạng
 *          thái · Giờ công · Giờ OT · Thao tác) + record-detail modal [18]/
 *          [18-RO] + confirm dialog [19], built against
 *          `SC-011-ATTENDANCE-API-CONTRACT-V1.0.md` (backend Phase 1, this
 *          same session). PHASE 2 (frontend) of that same task.
 *
 *          NOT built this phase (see file header @cross-references + PHASE 2
 *          task boundaries, mirroring API contract §6):
 *          - `[18e]` "Xử lý ngoại lệ giờ công" sub-block — no backend endpoint.
 *          - Bulk "Xử lý nhanh N bản ghi" (☐ checkbox column, header
 *            select-all) — no bulk endpoint; without it there is nothing to
 *            attach the checkbox column to (registry ties ☐ to bulk only).
 *          - (ĐÃ BUILD 2026-08-26) `[18-LOG]` "Lịch sử xử lý" popup — trước đây
 *            bỏ hẳn vì GET /manager/attendance KHÔNG trả `manager_resolved_at`/
 *            `approved_at`/`rejected_at`; nay endpoint đã trả (kèm tên người xử
 *            lý), popup dựng từ dữ liệu THẬT. Mốc "Công ghi nhận tự động" và
 *            "Nhân viên khiếu nại" VẪN chưa có nguồn dữ liệu ⇒ không bịa dòng
 *            (Zero-Mock) — xem AMENDMENT 2026-08-26 trong registry §2 SC-011.
 *          - (ĐÃ BUILD 2026-08-26) `[10]⚙` modal "Cấu hình cơ sở" — DD-0105 nay
 *            đủ 3 mảnh: cấp quyền (SC-004 `[9]`) · đặt giá trị (GET/PUT
 *            /manager/branch-workforce-config) · ĐỌC khi tính công
 *            (TenantWorkforceConfig.resolve_shift_match_thresholds). Nút vẫn ẩn
 *            hẳn khi cơ sở chưa được cấp quyền nào.
 *          - Standalone OT registration UI (old popover/menu) — the registry
 *            itself retires this from SC-011 ("OT giao trước" moved to the
 *            Schedule screen; "OT bù" routes through `[18e]`, not built here).
 *            "Giờ OT" column is read-only display of the pre-existing field.
 *          - Employee "Ghi chú của nhân viên" `[18-note]` — API response has
 *            no such field (only `manager_notes`, the MANAGER's own note) —
 *            always hidden (matches its own "hide when absent" rule, honestly
 *            for every record instead of guessing).
 *
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-17
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager — Chấm công (Vietnamese F&B Workforce Management)
 * - Impact: Revenue Critical — Giờ công/Giờ OT tự động ghi nhận (payroll-adjacent)
 * - Stakeholders: Branch Managers, Shift Managers, Head Chefs
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Page Component)
 * - Pattern: React + TanStack Query (useManagerAttendance hooks) — mọi data từ
 *   API thật (manager_attendance.py), loading skeleton + empty state + toast.
 *   Date filter: ContinuousScrollDatePicker (Luật 13). Tooltips: PortalTooltip
 *   (Luật 12).
 * - Dependencies: React Router, useManagerAttendance, useManagerStaff,
 *   useManagerSchedule (shift codes), NQH design tokens, xlsx (export)
 *
 * @cross-references
 * - API contract: docs/03-integrate/02-api-specifications/SC-011-ATTENDANCE-API-CONTRACT-V1.0.md
 * - UI/behavior SSOT: docs/02-design/04-ui-ux-design/persona-flows/
 *   WIREFRAME-V0-MASTER-REGISTRY.md § SC-011-ATTEND-MGR
 * - Luật 12 (portal tooltip) + Luật 13 (calendar cuộn dọc): docs/02-design/
 *   04-ui-ux-design/UI-UX-DECISION-REGISTER.md §2
 * - Nghiệm thu (TC + kỳ vọng): các mục TC trong registry § SC-011-ATTEND-MGR
 *   khối STATE/NHÃN VI ở trên — trước đây trỏ một checklist chỉ có ở máy cá
 *   nhân, không đọc được từ repo; nội dung đã gộp vào registry.
 * - Backend: backend/app/api/v2/endpoints/manager_attendance.py,
 *   backend/app/services/shift_evaluation_service.py
 * - Hooks: frontend/src/hooks/useManagerAttendance.ts
 * - Date picker: frontend/src/components/common/ContinuousScrollDatePicker.tsx
 * ===============================================================================
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { useAttendancePhoto, useBulkResolveAttendance, useResolveAttendance, useTeamAttendance } from "../../hooks/useManagerAttendance";
import { useManagerStaff } from "../../hooks/useManagerStaff";
import { useScheduleShiftCodes } from "../../hooks/useManagerSchedule";
import type {
  BranchWorkforceConfig,
  BranchWorkforceConfigValues,
  BulkResolveItem,
  ResolveRequest,
  TeamAttendanceParams,
  TeamAttendanceRow,
  AttendanceExceptionLine,
} from "../../services/api/managerApi";
import { managerApi } from "../../services/api/managerApi";
import { PortalTooltip } from "../../components/common/PortalTooltip";
import { PortalMenu, type PortalMenuAnchor } from "../../components/common/PortalMenu";
import {
  ContinuousScrollDatePicker,
  formatDateVi,
  type DateFilterValue,
} from "../../components/common/ContinuousScrollDatePicker";
import { useDebounce } from "../../hooks/useDebounce";
import { useAuthStore } from "../../store/auth";
import styles from "./ManagerAttendancePage.module.css";

// ── Icons (inline SVG — Heroicons paths, 0 emoji) ───────────────────────────

const InfoIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={14} height={14} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
  </svg>
);
const ChevronDownIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={16} height={16} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
);
/** 2026-09-07 (sync prototype `sc011-build-prototype.html` `IC.search`): icon
 *  kính lúp đứng TRƯỚC nhãn trên nút "Tìm nhân viên" — cùng path SVG với
 *  prototype, chỉ đổi width/height 16→14 khớp cỡ icon khác trong file này. */
const SearchIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={14} height={14} aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.3-4.3" />
  </svg>
);
/** 2026-09-07 (sync wireframe registry dòng 4417 `[1][📍 Địa điểm: BKL ▼]` +
 *  prototype `IC.pin`): icon ghim vị trí đứng TRƯỚC nhãn "Địa điểm" — ASCII
 *  giữ icon này ở CẢ 2 trường hợp (nhiều cơ sở lẫn "khoá cứng chỉ 1 cơ sở"),
 *  chỉ khác chevron ▾ (chỉ multi-select mới có, xem ANNOTATION `[1]`) — badge
 *  tĩnh trước đây thiếu hẳn icon, không có chevron là ĐÚNG (không phải dropdown
 *  khi khoá cứng 1 cơ sở). */
const PinIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={14} height={14} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0116 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={22} height={22} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);
/** [18-LOG] 🕐 — mở popup "Lịch sử xử lý". SVG inline theo đúng pattern
 *  CloseIcon/PhotoIcon sẵn có trong file (không thêm dependency icon mới). */
const HistoryIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={20} height={20} aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
  </svg>
);
const PhotoIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={28} height={28} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25z" />
  </svg>
);
const WarnIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={16} height={16} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.502-3.032-1.502-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

// ── Helpers ──────────────────────────────────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, "0");
const toISODate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const addDays = (iso: string, days: number) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toISODate(d);
};
// ── Asia/Ho_Chi_Minh-explicit datetime helpers ──────────────────────────────
// BUG THẬT đã sửa (2026-08-17, QA nghiệm thu SC-011 — cột "Thực tế" hiện
// "23:58–10:03" thay vì "07:00–17:00" trên trình duyệt/máy chủ KHÔNG ở múi
// giờ Asia/Ho_Chi_Minh): các hàm dưới đây TRƯỚC ĐÂY dùng `new Date(iso).
// getHours()`/`getMinutes()`/`setHours()` — các API này đọc/ghi theo múi giờ
// TRÌNH DUYỆT đang chạy (ambient), không phải Asia/Ho_Chi_Minh cố định. Đúng
// tình cờ trên máy đặt UTC+7 (VN), sai trên mọi máy khác — CÙNG HỌ bug với
// `late_policy_service.py` (xem `VN_TZ`/comment "BUG THẬT đã sửa 2026-08-10"
// ở đó) và `manager_attendance.py::_naive()` (comment "BUG THẬT đã sửa
// 2026-08-06"). Backend nay LUÔN trả `clock_in`/`clock_out`/
// `computed_check_in`/`computed_check_out` dạng ISO8601 CÓ múi giờ tường
// minh (xem `manager_attendance.py` — cùng PR) — nên input ở đây LUÔN có
// offset, `new Date(iso).getTime()` (đọc epoch ms) parse ĐÚNG bất kể ambient
// tz. Cái sai là bước SAU: đọc lại giờ-trong-ngày bằng getHours()/setHours()
// (ambient) thay vì quy đổi tường minh sang VN. Việt Nam KHÔNG có DST nên
// offset cố định +07:00 là chính xác — tránh phụ thuộc dữ liệu IANA tz-db
// (Intl.DateTimeFormat timeZone) vốn không đảm bảo có mặt trên mọi runtime.
const VN_OFFSET_MS = 7 * 60 * 60 * 1000;
/** Absolute instant (epoch ms từ 1 chuỗi ISO CÓ múi giờ) → giờ tường VN. */
const vnWallClock = (iso: string) => {
  const vn = new Date(new Date(iso).getTime() + VN_OFFSET_MS);
  return { y: vn.getUTCFullYear(), mo: vn.getUTCMonth(), d: vn.getUTCDate(), h: vn.getUTCHours(), m: vn.getUTCMinutes() };
};
/** Ngược lại: 1 mốc giờ-tường-VN (ngày + phút-trong-ngày) → epoch ms UTC thật. */
const vnWallClockToEpochMs = (y: number, mo: number, d: number, minutesOfDay: number) =>
  Date.UTC(y, mo, d, 0, 0, 0, 0) - VN_OFFSET_MS + minutesOfDay * 60_000;

export const formatTime = (iso: string | null) => {
  if (!iso) return "—:—";
  const { h, m } = vnWallClock(iso);
  return `${pad2(h)}:${pad2(m)}`;
};
export const formatTimeRange = (a: string | null, b: string | null) => `${formatTime(a)}–${formatTime(b)}`;
export const formatHoursVi = (hours: number) => hours.toFixed(1).replace(".", ",");
/** SC-011 modal [18] khối Ảnh/GPS — toạ độ thô, đúng định dạng ví dụ wireframe
 * ("10.93°N, 108.10°E"): 2 chữ số thập phân, hậu tố N/S theo dấu vĩ độ, E/W
 * theo dấu kinh độ (VN luôn dương/Bắc-Đông nhưng viết tổng quát, không hardcode). */
export const formatGpsCoords = (lat: number, lng: number): string =>
  `${Math.abs(lat).toFixed(2)}°${lat >= 0 ? "N" : "S"}, ${Math.abs(lng).toFixed(2)}°${lng >= 0 ? "E" : "W"}`;
const timeOfISO = (iso: string | null): string => {
  if (!iso) return "";
  const { h, m } = vnWallClock(iso);
  return `${pad2(h)}:${pad2(m)}`;
};
/** Kết hợp ngày ca (YYYY-MM-DD) + giờ nhập tay (HH:MM, giờ-tường-VN — Quản lý
 * gõ giờ VN) → ISO datetime (epoch UTC thật). Ca qua đêm: nếu Ra < Vào (theo
 * giờ-trong-ngày) → lùi Ra sang ngày kế tiếp. KHÔNG dùng `new Date(dateISO)`
 * ambient — tự parse Y/M/D bằng string split để không phụ thuộc múi giờ máy
 * chạy (xem block comment "Asia/Ho_Chi_Minh-explicit" ở trên). */
export const combineDraftToISO = (dateISO: string, hhmm: string, rollAfterHHMM?: string): string | null => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const [y, mo, da] = dateISO.split("-").map(Number);
  let minutesOfDay = h * 60 + m;
  if (rollAfterHHMM) {
    const [rh, rm] = rollAfterHHMM.split(":").map(Number);
    if (!Number.isNaN(rh) && (h < rh || (h === rh && m < rm))) {
      minutesOfDay += 24 * 60;
    }
  }
  return new Date(vnWallClockToEpochMs(y, mo - 1, da, minutesOfDay)).toISOString();
};
/** Làm tròn về mốc :00/:30 gần nhất (theo giờ tường VN) — mirror
 * `round_to_nearest_half_hour()` (shift_evaluation_service.py), tie-break
 * XUỐNG, cho nút "Ghi nhận theo giờ". */
export const roundToHalfHourISO = (iso: string): string => {
  const { y, mo, d, h, m } = vnWallClock(iso);
  const totalMin = h * 60 + m;
  const lower = Math.floor(totalMin / 30) * 30;
  const upper = lower + 30;
  const rounded = totalMin - lower <= upper - totalMin ? lower : upper;
  return new Date(vnWallClockToEpochMs(y, mo, d, rounded)).toISOString();
};
const round2 = (n: number) => Math.round(n * 100) / 100;

/** 7 tooltip cột header + 2 tooltip [9b] — copy VERBATIM checklist §4.2 /
 * registry khối [HEADER-TOOLTIP] — KHÔNG diễn giải lại (QA string-match). */
export const COL_TOOLTIPS = {
  ca: "Ca làm việc theo lịch phân công.",
  thucTe: "Giờ chấm công thực tế (Vào/Ra thô, chưa xử lý).",
  // #851 / #1190: rounding is OFF by default; it is a tenant setting
  // (round_computed_times) that this page does not read yet, so the wording is
  // neutral and true in both modes.
  duocTinh: "Giờ dùng để tính công — mặc định bằng giờ chấm thực tế (không làm tròn), trừ khi công ty bật làm tròn hoặc Quản lý tự chỉnh tay.",
  diaDiem: "Vị trí GPS lúc chấm công, đối chiếu bán kính cho phép của cơ sở.",
  trangThai: "Kết quả đối chiếu giờ chấm với ca: Đúng giờ / Đi trễ / Về sớm / Không khớp ca.",
  gioCong: "Tổng giờ công được tính trong ca (chưa gồm OT).",
  gioOt: "Tổng giờ làm thêm đã xác nhận, ngoài giờ ca chính.",
};
const EXPORT_ALL_TOOLTIP = "Chặn xuất dữ liệu nếu còn dòng Chờ xử lý trong khoảng đang lọc — xử lý hết trước khi xuất.";

/** 2026-09-16 (yêu cầu trực tiếp) — cho Quản lý TỰ kéo-giãn độ rộng từng cột
 *  bảng "Chấm công". Kéo hẹp 1 cột lại → CHỮ THƯỜNG trong cột đó được phép
 *  xuống dòng (chấp nhận được theo đúng yêu cầu) — RIÊNG nhãn "Trạng thái"
 *  (class `.lbl`, xem CSS) LUÔN `white-space: nowrap`, không vỡ chữ giữa
 *  nhãn dù cột hẹp tới đâu. Không có cột riêng cho checkbox (đã gộp vào
 *  "Thao tác", 2026-09-13) nên chỉ 9 khoá, đúng 9 cột thật đang render. Độ
 *  rộng mặc định ước theo ngân sách 9-cột/~1200px đã ghi ở CSS `.attWrap`,
 *  tại font-size mới (12px, 2026-09-16). "act" (Thao tác) KHÔNG có tay kéo
 *  (cột cuối, giữ cố định đủ chỗ checkbox+nút) — xem `renderHeader`. */
type AttColKey = "emp" | "ca" | "thucTe" | "duocTinh" | "diaDiem" | "trangThai" | "gioCong" | "gioOt" | "act";
// 2026-09-19 (yêu cầu trực tiếp, tiếp): sàn 60px CỐ ĐỊNH không đủ cho nhãn
// dài như "ĐƯỢC TÍNH ⓘ"/"TRẠNG THÁI ⓘ" — kéo hẹp gần sàn đó làm nhãn cột bị
// che khuất (đã thấy ở ảnh chụp: "TRẠNG TI", "ĐƯỢC TÍN"). Sàn ĐÚNG phải RIÊNG
// theo từng cột = độ rộng THẬT của "chữ + icon tooltip" cột đó (đo qua DOM,
// xem `colLabelRefs`/effect đo lúc mount bên dưới), không phải 1 số đoán
// chung cho mọi cột. `MIN_COL_WIDTH` giữ lại làm SÀN DỰ PHÒNG (trước khi đo
// xong, hoặc lỡ đo ra 0 vì ref chưa gắn) — không còn là sàn áp dụng trực
// tiếp khi đã có số đo thật.
const MIN_COL_WIDTH = 60;
// Padding ngang 2 bên mỗi ô = 2 × var(--nqh-space-3) (12px, nqh-design-tokens.css)
// — cộng vào độ rộng THẬT của nhãn để ra sàn resize, tránh nhãn dính sát viền ô.
const HEADER_CELL_PADDING_X = 24;
const DEFAULT_COL_WIDTHS: Record<AttColKey, number> = {
  emp: 150,
  ca: 150,
  thucTe: 110,
  duocTinh: 110,
  diaDiem: 120,
  trangThai: 170,
  gioCong: 110,
  gioOt: 110,
  act: 150,
};
/** BOT-050 (#839, 2026-09-14) — filter "Trạng thái" [FE wiring only, backend
 *  `status_filter` đã có sẵn]. CHỈ 7 mã có nhãn VI đã chốt (`STATUS_LABEL_VI`
 *  backend, verbatim — trùng đúng chữ đang hiện ở cột "Trạng thái" cùng
 *  trang). KHÔNG đưa vào đây 3 alias backend cũng chấp nhận nhưng CHƯA có
 *  quyết định sản phẩm rõ ràng cho 1 dropdown riêng — cần @ba/@designer chốt
 *  trước khi thêm: "pending" (trùng khái niệm với chip "N cần xử lý" đã có
 *  sẵn — 2 cơ chế lọc "cần xử lý" khác hành vi dễ gây nhầm), "flagged" (chưa
 *  có nhãn VI hiển thị-cho-người-dùng nào cho khái niệm này trong trang hiện
 *  tại, chỉ có động từ "Gắn cờ" trong code comment, không phải UI copy đã
 *  duyệt), "upcoming" (không có nhãn VI nào từng hiển thị cho ca chưa tới
 *  giờ). Xem MANAGER-FLOW-SCREENS-ISSUES-TRACKER.md §Bước 2. */
const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "on_time", label: "Đúng giờ" },
  { value: "late", label: "Đi trễ" },
  { value: "early_leave", label: "Về sớm" },
  { value: "unmatched", label: "Không khớp ca" },
  { value: "missed_check_out", label: "Quên chấm ra" },
  { value: "off_schedule", label: "Chấm công không theo ca" },
  { value: "missing", label: "Thiếu chấm công" },
];
/** Nhãn trạng thái nói về LỆCH GIỜ (khác "Có khiếu nại"/"Thiếu chấm công") —
 *  nhiều nhãn cùng lúc = record vừa lệch đầu vào vừa lệch đầu ra. */
const DEVIATION_STATUS_CODES = new Set([
  "late",
  "early_leave",
  "missed_check_out",
  "unmatched",
  "off_schedule",
  // "Lệch GPS" (StatusLabel.GPS_DEVIATION) — cộng THÊM như "Có khiếu nại"
  // (backend: applied by caller từ GPSAttendanceEvidence, không loại trừ nhãn
  // gốc), nên record vừa lệch giờ vừa lệch GPS mang CẢ 2 nhãn cùng lúc, mỗi
  // nhãn cần quyết định riêng — thiếu dòng này khiến "Lệch GPS" không bao giờ
  // được Quản lý xác nhận đã xem qua trước khi ghi nhận công.
  "gps_deviation",
]);

/** Màu nhãn "Trạng thái" — khớp `labelHtml()` trong prototype
 * `sc011-build-prototype.html` (dòng ~802-810, 3 nhóm `lbl-ok`/`lbl-warn`/
 * `lbl-bad` + mặc định `lbl-neutral`). "off_schedule" ("Chấm công không theo
 * ca") KHÔNG nằm trong danh sách warn/bad của chính prototype — rơi về
 * neutral đúng như bản gốc, không tự nâng cấp. */
export type StatusLabelColor = "ok" | "warn" | "bad" | "neutral";
export function statusLabelColor(code: string): StatusLabelColor {
  if (code === "on_time") return "ok";
  if (code === "late" || code === "early_leave" || code === "missed_check_out") return "warn";
  if (code === "unmatched" || code === "missing" || code === "complaint" || code === "gps_deviation") return "bad";
  return "neutral";
}

const EXCEPTION_BLOCK_TOOLTIP =
  "'Ngoài giờ' = khoảng giờ có Từ–Đến cụ thể (OT). 'Điều chỉnh' = cộng/trừ giờ công trực tiếp, không gắn khung giờ.";
const EXPORT_RECORDED_TOOLTIP = "Xuất dữ liệu ngay, bỏ qua các dòng Chờ xử lý.";

export const isPendingRow = (r: TeamAttendanceRow) => r.action_state === "pending_review";

const STATUS_LABEL_COLOR_CLASS: Record<StatusLabelColor, string> = {
  ok: "lblOk",
  warn: "lblWarn",
  bad: "lblBad",
  neutral: "lblNeutral",
};

/** [Trạng thái] badge màu — dùng chung cho bảng, thẻ mobile, và tóm tắt trong
 * modal (3 chỗ gọi `labelHtml()` trong prototype). Mỗi nhãn 1 badge màu
 * riêng — 2026-09-16 (yêu cầu trực tiếp): BỎ dấu "·" nối giữa các nhãn (màu
 * nền riêng của mỗi badge đã đủ phân biệt, dấu chấm thừa — đặc biệt xấu khi
 * nhãn xuống hàng, dấu chấm treo lơ lửng cuối dòng trên). Khoảng cách giữa
 * các nhãn (kể cả khi xuống hàng) chuyển sang `gap` của flex-wrap thay vì
 * span dấu chấm chèn giữa. */
const StatusLabels: React.FC<{ codes: string[]; labelsVi: string[] }> = ({ codes, labelsVi }) => {
  if (labelsVi.length === 0) return <>—</>;
  return (
    <span className={styles.lblGroup}>
      {labelsVi.map((vi, i) => (
        <span key={`${codes[i]}-${i}`} className={`${styles.lbl} ${styles[STATUS_LABEL_COLOR_CLASS[statusLabelColor(codes[i])]]}`}>
          {vi}
        </span>
      ))}
    </span>
  );
};

/** Khoá ổn định cho 1 dòng — cùng quy tắc dùng ở mọi nơi khác trên trang
 *  (`key={...}` bảng/thẻ, so trùng dòng ở `overlapRow`): ưu tiên
 *  `attendance_ref` (id thật hoặc "schedule:<id>" cho "Thiếu chấm công"),
 *  fallback ghép lịch+nhân viên khi cả 2 đều rỗng. */
export const rowKeyOf = (r: TeamAttendanceRow) => r.attendance_ref ?? `${r.schedule_id}-${r.user_id}`;

/** Ngưỡng CFG-20..23 cần để diễn giải luật gán nhãn bằng số liệu THẬT của cơ
 *  sở đang xem (khác với hard-code số phút chung chung) — cùng field đã có
 *  sẵn trên `TeamAttendanceData`, tách interface riêng để `explainStatusLabel`
 *  test được độc lập, không phải import cả response type đầy đủ. */
export interface AttendanceRuleThresholds {
  check_in_tolerance_minutes: number;
  check_out_early_tolerance_minutes: number;
  missed_check_in_threshold_minutes: number;
  missed_check_out_threshold_minutes: number;
  gps_radius_meters: number;
}

export interface StatusLabelRuleExplanation {
  /** Mã luật NỘI BỘ — không phải chuỗi hiển thị cho nhân viên, chỉ để Quản lý
   *  đối chiếu/báo lỗi với đội kỹ thuật khi thấy nhãn gán "lạ". */
  code: string;
  labelVi: string;
  rule: string;
  managerNote: string;
}

/** [Tra cứu luật gán nhãn] — diễn giải bằng tiếng Việt luật đứng sau MỖI mã
 * `StatusLabel` (khớp `backend/app/services/shift_evaluation_service.py` +
 * phần cộng nhãn additive trong `manager_attendance.py`), kèm ngưỡng CẤU HÌNH
 * THẬT của cơ sở đang xem — không hard-code số phút, tránh giải thích sai khi
 * cơ sở đã đổi dung sai qua "Cấu hình cơ sở" `[10]⚙`. */
export function explainStatusLabel(
  code: string,
  labelVi: string,
  cfg: AttendanceRuleThresholds,
): StatusLabelRuleExplanation {
  switch (code) {
    case "on_time":
      return {
        code: "ATT-R1-ONTIME",
        labelVi,
        rule: `Giờ chấm Vào không trễ quá ${cfg.check_in_tolerance_minutes} phút VÀ giờ chấm Ra không sớm quá ${cfg.check_out_early_tolerance_minutes} phút so với giờ ca — cả 2 điều kiện cùng đạt.`,
        managerNote: "Không phải nhãn lệch giờ — không cần xử lý gì ở khối \"Xử lý ngoại lệ\".",
      };
    case "late":
      return {
        code: "ATT-R2-LATE",
        labelVi,
        rule: `Giờ chấm Vào trễ hơn giờ ca bắt đầu quá ${cfg.check_in_tolerance_minutes} phút (dung sai đi trễ hiện tại của cơ sở).`,
        managerNote:
          'Nhãn lệch giờ — bắt buộc chọn "Điều chỉnh" (trừ giờ) hoặc "Bỏ qua" ở khối "Xử lý ngoại lệ giờ công" trước khi Ghi nhận công.',
      };
    case "early_leave":
      return {
        code: "ATT-R3-EARLY",
        labelVi,
        rule: `Giờ chấm Ra sớm hơn giờ ca kết thúc quá ${cfg.check_out_early_tolerance_minutes} phút (dung sai về sớm hiện tại của cơ sở).`,
        managerNote: 'Nhãn lệch giờ — bắt buộc chọn "Điều chỉnh"/"Bỏ qua" trước khi Ghi nhận công.',
      };
    case "missed_check_out":
      return {
        code: "ATT-R4-NOOUT",
        labelVi,
        rule: `Có chấm Vào nhưng không có chấm Ra trong vòng ${cfg.missed_check_out_threshold_minutes} phút sau giờ ca kết thúc.`,
        managerNote:
          'Nhãn lệch giờ — nên xác nhận giờ Ra thật với nhân viên trước khi "Điều chỉnh"/"Bỏ qua".',
      };
    case "off_schedule":
      return {
        code: "ATT-R5-OFFSCH",
        labelVi,
        rule: "Có dữ liệu chấm công trong ngày nhưng KHÔNG khớp với bất kỳ ca nào được xếp cho nhân viên này.",
        managerNote: 'Nhãn lệch giờ — kiểm tra lại lịch phân ca trước khi "Điều chỉnh"/"Bỏ qua".',
      };
    case "unmatched":
      return {
        code: "ATT-R6-UNMATCH",
        labelVi,
        rule: "Có ca được xếp cho ngày này nhưng giờ chấm công lệch quá xa khung ca (vượt ngưỡng khớp ca) nên hệ thống không tự khớp được — cần Quản lý đối chiếu tay.",
        managerNote: 'Nhãn lệch giờ — bắt buộc chọn "Điều chỉnh"/"Bỏ qua" trước khi Ghi nhận công.',
      };
    case "missing":
      return {
        code: "ATT-R7-MISSING",
        // 2026-09-08 (user chỉnh lại câu chữ): KHÔNG phải "0 bản ghi chấm
        // công" — có thể có bản ghi, chỉ là giờ Vào của nó rơi NGOÀI khung
        // được phép ánh xạ vào ca này (`match_day()` §3.6: schedule còn lại
        // trong `unclaimed` sau khi đã thử khớp mọi bản ghi trong ngày —
        // record không khớp ca nào rơi RIÊNG thành "Không khớp ca"/"Chấm
        // công không theo ca", KHÔNG lấp được ca này).
        labelVi,
        rule: `Ca này không có bản ghi chấm công nào có giờ Vào rơi trong khung được phép ánh xạ ca — từ ${cfg.check_in_tolerance_minutes} phút TRƯỚC giờ ca đến ${cfg.missed_check_in_threshold_minutes} phút SAU giờ ca. Bản ghi chấm công trước/sau khung này (nếu có) không được tính cho ca — nó rơi thành "Không khớp ca"/"Chấm công không theo ca" ở dòng riêng, còn ca vẫn hiện "Thiếu chấm công".`,
        managerNote: "Không phải nhãn lệch giờ — Quản lý ghi nhận tay theo ca/theo giờ, hoặc chọn Không ghi nhận.",
      };
    case "complaint":
      return {
        code: "ATT-R8-COMPLAIN",
        labelVi,
        rule: "Nhân viên đã gửi khiếu nại về bản ghi này. Nhãn CỘNG THÊM — không thay thế nhãn gốc (record vẫn giữ nguyên nhãn Đúng giờ/Đi trễ/... đã có).",
        managerNote:
          'Luôn đẩy Thao tác về "Xác nhận công" để Quản lý xem lại, kể cả khi record đã tự động "Đã ghi nhận" từ trước.',
      };
    case "gps_deviation":
      return {
        code: "ATT-R9-GPS",
        labelVi,
        rule: `Vị trí GPS lúc chấm công nằm ngoài bán kính cho phép (${cfg.gps_radius_meters}m quanh cơ sở). Nhãn CỘNG THÊM — không thay thế nhãn gốc.`,
        managerNote:
          'Vẫn là nhãn lệch giờ đi kèm — bắt buộc chọn "Điều chỉnh"/"Bỏ qua" riêng ở khối "Xử lý ngoại lệ" như mọi nhãn lệch giờ khác.',
      };
    default:
      return {
        code: "ATT-R0-UNKNOWN",
        labelVi,
        rule: "Hệ thống chưa có luật giải thích cho mã nhãn này.",
        managerNote: "Báo đội kỹ thuật nếu nhãn này lặp lại nhiều lần.",
      };
  }
}

/**
 * rowSpan `[8]` — gộp ô ☐/Nhân viên/[17] khi mọi dòng trong nhóm CÙNG action_state,
 * NHƯNG KHÔNG gộp khi nhãn dùng chung là "Xác nhận công" (pending_review): đây có
 * thể là 2+ bản ghi CẦN XỬ LÝ RIÊNG (khác Ca/GPS/ghi chú), và nút gộp chỉ mở được
 * bản ghi ĐẦU nhóm — bản ghi thứ 2 trở đi không có cách nào mở từ bảng. Chỉ gộp
 * an toàn khi nhãn là 1 trong 2 nhãn CHỈ-ĐỌC (✓ Đã ghi nhận / ✕ Không ghi nhận),
 * nơi mở nhầm bản ghi khác trong nhóm rủi ro thấp hơn hẳn.
 * Gap-analysis 2026-09-07 — đúng "Tình huống MỚI" đã điều tra ở BOT-023 (#712).
 */
export const canMergeActionCell = (rows: TeamAttendanceRow[]): boolean =>
  rows.length > 0 && rows.every((r) => r.action_state === rows[0].action_state) && !isPendingRow(rows[0]);

/**
 * [18-LOG] Dựng danh sách mốc "Lịch sử xử lý" từ các field audit backend trả về.
 *
 * Tách hàm thuần (không JSX/hook) để test được không cần render. Chỉ liệt kê mốc
 * THẬT SỰ có trong dữ liệu — record chưa ai đụng tới thì trả mảng rỗng, KHÔNG bịa
 * dòng "hệ thống ghi nhận" nào (Zero-Mock: trước đây popup này bị bỏ hẳn vì
 * endpoint không trả các field này, không phải vì thiếu thiết kế).
 */
export const buildAuditTimeline = (r: TeamAttendanceRow) => {
  const items: { at: string; label: string; actor: string | null; detail?: string | null }[] = [];
  if (r.auto_recorded_at) {
    // Registry [18-LOG] ví dụ Lê Thị Vân: mốc đầu tiên trong MỌI timeline của
    // record tự-động-ghi-nhận, actor "Hệ thống", detail kèm nhãn Trạng thái +
    // cặp giờ được tính TẠI THỜI ĐIỂM ĐÓ ("Về sớm, được tính 10:00–18:00").
    // `resolution_source` chuyển sang MANAGER hoặc bị khiếu nại đè lên SAU đó
    // không xoá mốc này — nó ghi lại sự kiện ĐÃ XẢY RA, không phải trạng thái
    // hiện tại.
    // "Có khiếu nại"/"Lệch GPS" CỘNG THÊM sau, ở lần đọc CHÍNH request này —
    // không thuộc về những gì hệ thống hiểu TẠI THỜI ĐIỂM tự-động-ghi-nhận,
    // loại khỏi detail (2 nhãn đó có dòng log riêng của chính chúng bên dưới).
    const baseLabelsVi = r.status_labels
      .map((code, i) => (code === "complaint" || code === "gps_deviation" ? null : r.status_labels_vi[i]))
      .filter((v): v is string => Boolean(v));
    items.push({
      at: r.auto_recorded_at,
      label: "Công ghi nhận tự động",
      actor: "Hệ thống",
      detail: `${baseLabelsVi.join(", ")}, được tính ${formatTimeRange(r.computed_check_in, r.computed_check_out)}`,
    });
  }
  if (r.complaint_raised_at) {
    // Registry [18-LOG] ví dụ Lê Thị Vân: actor = CHÍNH nhân viên của bản ghi
    // (không phải "Hệ thống"/Quản lý); nội dung khiếu nại DÙNG CHUNG `notes`
    // (Ghi chú của nhân viên) — đúng ví dụ registry (1 câu vừa là "Ghi chú
    // của nhân viên" vừa là nội dung dòng "Nhân viên khiếu nại", không tách
    // cột riêng). `has_complaint=true` nhưng `complaint_raised_at=null`
    // (record cũ trước khi cột này tồn tại) → KHÔNG có mốc thật, ẩn hẳn dòng
    // này thay vì bịa timestamp (Zero-Mock).
    items.push({
      at: r.complaint_raised_at,
      label: "Nhân viên khiếu nại",
      actor: r.full_name,
      detail: r.employee_note || null,
    });
  }
  if (r.manager_resolved_at) {
    // 2026-09-07 (sync wireframe): khối NHÃN VI chuẩn (registry dòng 5478-5479)
    // liệt "Quản lý duyệt" cho đúng mốc [18-LOG] này (nhóm cùng "Lịch sử xử
    // lý"/"Công ghi nhận tự động"/"Nhân viên khiếu nại") — khác "Quản lý xử
    // lý" trong cụm "Đã ghi nhận (Quản lý xử lý)" (dòng phụ suffix trong thân
    // modal, xem :1421, giữ nguyên, KHÔNG đụng — đây là 1 câu khác, không
    // phải cùng field). Wireframe tự mâu thuẫn nội bộ 1 chỗ khác (dòng 5012
    // văn xuôi vẫn ghi "Quản lý xử lý") — theo khối NHÃN VI (nguồn chuẩn cho
    // string-match) làm SSOT, không theo văn xuôi.
    items.push({ at: r.manager_resolved_at, label: "Quản lý duyệt", actor: r.manager_resolved_by_name ?? null });
  }
  if (r.approved_at) {
    items.push({ at: r.approved_at, label: "Đã duyệt", actor: r.approved_by_name ?? null });
  }
  if (r.rejected_at) {
    items.push({ at: r.rejected_at, label: "Đã từ chối", actor: r.rejected_by_name ?? null });
  }
  // CŨ → MỚI (mốc mới nhất ở DƯỚI CÙNG). Đây là quy ước RIÊNG của popup này,
  // chốt tại WIREFRAME-V0-MASTER-REGISTRY.md §2 SC-011 khối [18-LOG]: "đọc như
  // hội thoại, KHÁC quy ước 'mới nhất lên đầu' của danh sách log defer". Đừng
  // đổi sang desc cho 'nhất quán' — sẽ lệch SSOT.
  return items.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
};

/**
 * [10]⚙ Modal "Cấu hình cơ sở" — DD-0105 Tầng 2, phần ĐẶT GIÁ TRỊ.
 *
 * Chỉ render KHỐI ĐÃ ĐƯỢC CẤP QUYỀN, đúng registry §2 SC-011 `[10]`: "mở modal
 * nhỏ, CHỈ hiện khối tương ứng quyền ĐÃ được cấp". Không hiện khối chỉ-đọc —
 * nút mở modal vốn đã ẩn khi không có quyền nào, nên vào được đây nghĩa là đã
 * có ít nhất 1 khối để hiện.
 *
 * Dữ liệu do trang tải sẵn (trang cần biết quyền TRƯỚC để quyết định hiện nút),
 * truyền xuống qua `initial` — tránh gọi endpoint 2 lần.
 */
const BranchConfigModal: React.FC<{
  initial: BranchWorkforceConfig;
  onClose: () => void;
  onSaved: (d: BranchWorkforceConfig) => void;
}> = ({ initial, onClose, onSaved }) => {
  const [data, setData] = useState<BranchWorkforceConfig | null>(initial);
  const [draft, setDraft] = useState<Partial<BranchWorkforceConfigValues>>({});
  const [error, setError] = useState<string | null>(null);
  // Khối "Dung sai" — wireframe SC-011 [10]⚙: mặc định KHOÁ (chỉ đọc, hiện "Mặc
  // định hệ thống") cho tới khi tick "Tự đặt dung sai riêng cho cơ sở này".
  // Khởi tạo theo trạng thái override HIỆN CÓ, để cơ sở đã tự đặt trước đó vẫn
  // thấy đúng ô của mình đang mở, không bị khoá nhầm khi mở lại modal.
  const [toleranceOverrideOn, setToleranceOverrideOn] = useState(() =>
    (["check_in_tolerance_minutes", "check_out_early_tolerance_minutes", "missed_check_in_threshold_minutes"] as const).some(
      (k) => k in initial.override,
    ),
  );
  const [saving, setSaving] = useState(false);

  const value = <K extends keyof BranchWorkforceConfigValues>(k: K) =>
    (draft[k] ?? data?.effective[k]) as BranchWorkforceConfigValues[K];

  const save = async () => {
    if (!Object.keys(draft).length) return onClose();
    setSaving(true);
    setError(null);
    try {
      const updated = await managerApi.updateBranchWorkforceConfig(draft);
      setData(updated);
      setDraft({});
      onSaved(updated);
      onClose();
    } catch (e: any) {
      // Hiện NGUYÊN VĂN lý do backend trả (vd "Cơ sở chưa được cấp quyền sửa
      // các mục: …") — 403 ở đây là quyết định phân quyền có chủ đích, người
      // dùng cần biết vì sao, không nuốt thành "Lưu thất bại" chung chung.
      setError(e?.response?.data?.detail ?? e?.message ?? "Lưu không thành công");
    } finally {
      setSaving(false);
    }
  };

  const numField = (k: keyof BranchWorkforceConfigValues, label: string, editable: boolean) => (
    <label style={{ display: "grid", gap: 4 }}>
      <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
      <input
        type="number"
        min={1}
        disabled={!editable || saving}
        value={String(value(k) ?? "")}
        onChange={(e) => setDraft((d) => ({ ...d, [k]: Number(e.target.value) }))}
        style={{ padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14 }}
      />
    </label>
  );

  const boolField = (k: keyof BranchWorkforceConfigValues, label: string, editable: boolean) => (
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, minHeight: 30 }}>
      <input
        type="checkbox"
        disabled={!editable || saving}
        checked={Boolean(value(k))}
        onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.checked }))}
      />
      {label}
    </label>
  );

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sc011CfgTitle"
        style={{ maxWidth: 520 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHead}>
          <h2 className={styles.modalTitle} id="sc011CfgTitle">
            Cấu hình cơ sở{data?.branch_name ? ` — ${data.branch_name}` : ""}
          </h2>
          <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.modalBody}>
          {error && (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginBottom: 12 }}>
              {error}
            </div>
          )}
          {data && (
            <div style={{ display: "grid", gap: 18 }}>
              {/* Khối chỉ render khi ĐƯỢC CẤP QUYỀN — registry §2 SC-011 `[10]`.
                  2 quyền ĐỘC LẬP (bảng `[9]` SC-004), nên 1 cơ sở có thể chỉ
                  thấy 1 trong 2 khối. THỨ TỰ khối (2026-09-07, sync wireframe
                  đăng ký dòng 4721-4738): "Tự động ghi nhận" LUÔN đứng TRƯỚC
                  "Dung sai xác định trạng thái" — trước đây bị đảo ngược. */}
              {data.editable.auto_record && (
                <section style={{ display: "grid", gap: 6 }}>
                  {/* Wireframe/prototype dùng icon ⓘ hover-tooltip cạnh tiêu đề
                      (Luật 12 — cùng PortalTooltip đã dùng cho tiêu đề cột
                      bảng/menu Xuất trong file này), KHÔNG phải đoạn giải
                      thích hiện thường trực gây rối màn hình. */}
                  <strong style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                    Tự động ghi nhận công
                    <PortalTooltip
                      text="Baseline tenant hiện hành (đặt ở Cài đặt chấm công của Tenant Admin) — bật dòng nào ở đây = override RIÊNG cho cơ sở này, khác với baseline. Tắt = dùng đúng baseline tenant."
                      className={styles.infoIco}
                      tabIndex={0}
                      ariaLabel="Giải thích khối Tự động ghi nhận công"
                    >
                      <InfoIcon />
                    </PortalTooltip>
                  </strong>
                  {/* 2026-09-07 (sync wireframe): 2/4 nhãn thiếu phần giải
                      thích trong ngoặc so với đăng ký — bổ sung đúng nguyên
                      văn. */}
                  {boolField("auto_record_missed_check_out", 'Quên chấm ra (Ra=Vào tự fill; bật = "Đã ghi nhận" ngay, không cần bạn duyệt tay)', true)}
                  {boolField("auto_record_missing", "Thiếu chấm công (0 bản ghi cả ngày)", true)}
                  {boolField("auto_record_off_schedule", "Chấm công không theo ca", true)}
                  {boolField("auto_record_unmatched", "Không khớp ca nào", true)}
                  <div className={styles.confirmWarn} style={{ fontSize: 12 }}>
                    <WarnIcon /> Bật tính năng này có thể ghi nhận nhầm công cho giờ chấm không hợp
                    lệ — chỉ bật nếu quy trình chấm công tại cơ sở đã ổn định.
                  </div>
                </section>
              )}
              {data.editable.tolerance && (
                <section style={{ display: "grid", gap: 10 }}>
                  <strong style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                    Dung sai xác định trạng thái
                    <PortalTooltip
                      text='Đây là NGƯỠNG (số phút) — quyết định bao nhiêu phút thì tính lệch. Cách hệ thống dùng ngưỡng để kết luận trạng thái (thứ tự đánh giá, khi nào "Không khớp ca") là quy tắc cố định, không chỉnh ở đây.'
                      className={styles.infoIco}
                      tabIndex={0}
                      ariaLabel="Giải thích khối Dung sai xác định trạng thái"
                    >
                      <InfoIcon />
                    </PortalTooltip>
                  </strong>
                  {!toleranceOverrideOn && (
                    <div style={{ fontSize: 13 }}>
                      Mặc định hệ thống (áp mọi cơ sở chưa tự đặt riêng): Check-in{" "}
                      {data.tenant_baseline.check_in_tolerance_minutes} phút · Check-out (2 phía){" "}
                      {data.tenant_baseline.check_out_early_tolerance_minutes} phút · Quên chấm vào{" "}
                      {data.tenant_baseline.missed_check_in_threshold_minutes} phút
                    </div>
                  )}
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                    <input
                      type="checkbox"
                      checked={toleranceOverrideOn}
                      disabled={saving}
                      onChange={(e) => setToleranceOverrideOn(e.target.checked)}
                    />
                    Tự đặt dung sai riêng cho cơ sở này
                  </label>
                  {toleranceOverrideOn && (
                    <>
                      {numField("check_in_tolerance_minutes", "Dung sai check-in (phút)", true)}
                      {numField("check_out_early_tolerance_minutes", "Dung sai check-out (2 phía) (phút)", true)}
                      {numField("missed_check_in_threshold_minutes", "Ngưỡng quên chấm vào (phút)", true)}
                      <div className={styles.confirmWarn} style={{ fontSize: 12 }}>
                        <WarnIcon /> Ảnh hưởng TRỰC TIẾP nhãn "Đúng giờ / Đi trễ / Quên chấm ra /
                        Không khớp ca nào" của MỌI bản ghi tại cơ sở này kể từ khi lưu.
                      </div>
                    </>
                  )}
                </section>
              )}
            </div>
          )}
        </div>
        <div className={styles.modalFoot}>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onClose} disabled={saving}>
            Hủy
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={save}
            disabled={saving || !data}
          >
            {saving ? "Đang lưu…" : "Lưu thay đổi"}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Số giờ của 1 khung "Ngoài giờ" [18e]. `to` < `from` = khung qua nửa đêm. */
export function exceptionSpanHours(from?: string | null, to?: string | null): number {
  if (!from || !to) return 0;
  const [fh, fm] = from.split(":").map(Number);
  const [th, tm] = to.split(":").map(Number);
  let minutes = th * 60 + tm - (fh * 60 + fm);
  if (minutes < 0) minutes += 24 * 60;
  return Math.round((minutes / 60) * 100) / 100;
}

/** Giờ công/OT "tạm tính" từ 1 bộ (Được tính vào/ra + [18e]) — CÙNG công thức
 * server áp khi lưu (registry khối "Kết quả (tạm tính)"). Dùng chung cho dialog
 * [19] đơn (qua state draft trong component) và dialog xác nhận bulk (qua
 * `row.computed_check_in/out/exceptions` — KHÔNG có field điền-giùm ở bulk,
 * nên preview phải tính thẳng từ giá trị hệ thống đã tự tính sẵn trên row). */
function previewHoursFor(
  computedCheckIn: string | null,
  computedCheckOut: string | null,
  exceptions: AttendanceExceptionLine[],
): { workHours: number; otHours: number } {
  const adjustmentHours = round2(
    exceptions.filter((e) => e.kind === "adjustment").reduce((sum, e) => sum + (e.hours ?? 0), 0),
  );
  const otHours = round2(
    exceptions.filter((e) => e.kind === "overtime").reduce((sum, e) => sum + exceptionSpanHours(e.from_time, e.to_time), 0),
  );
  const baseHours =
    computedCheckIn && computedCheckOut
      ? Math.max(0, round2((new Date(computedCheckOut).getTime() - new Date(computedCheckIn).getTime()) / 3_600_000))
      : 0;
  return { workHours: round2(baseHours + adjustmentHours), otHours };
}

/** Quy tắc OT-VALIDATE + ADJUST-VALIDATE [18e]; null = dòng hợp lệ. */
export function validateExceptionLine(line: AttendanceExceptionLine): string | null {
  if (line.kind === "overtime") {
    if (!line.from_time || !line.to_time) return "Thiếu Từ/Đến.";
    if (exceptionSpanHours(line.from_time, line.to_time) <= 0) return "Đến phải sau Từ.";
    return null;
  }
  if (!line.hours) return "Giá trị điều chỉnh phải khác 0.";
  if (!(line.note ?? "").trim()) return "Điều chỉnh phải có Lý do.";
  return null;
}

/** Bề rộng popup [18-LOG] — dùng chung cho khung popup VÀ phép tính
 *  còn-chỗ-bên-phải/bên-trái modal khi mở nó. */
const AUDIT_POPUP_WIDTH = 380;

/** [18-LOG] Popup "Lịch sử xử lý" — mở từ 🕐 góc trên PHẢI của modal [18]/[18-RO].
 * 2026-09-07 (sync wireframe dòng 4982-4984): popup NHỎ (~380-420px) đè lên
 * góc phải modal cha, KHÔNG dim/đóng modal cha phía sau — trước đây tái dùng
 * `.modalOverlay` full-dim/center (đúng cho modal thật, sai cho popup này).
 * Đổi sang `PortalMenu` (cùng kỹ thuật portal + neo theo rect nút mở đã dùng
 * cho PortalTooltip/menu khác trong repo — không viết lại logic định vị). */
const AuditLogPopup: React.FC<{ row: TeamAttendanceRow; anchor: PortalMenuAnchor; onClose: () => void }> = ({ row, anchor, onClose }) => {
  const items = buildAuditTimeline(row);
  return (
    <PortalMenu anchor={anchor} onClose={onClose} minWidth={AUDIT_POPUP_WIDTH} style={{ maxWidth: AUDIT_POPUP_WIDTH, padding: "var(--nqh-space-3)" }}>
      <div role="dialog" aria-modal="false" aria-labelledby="sc011AuditTitle">
        <div className={styles.modalHead} style={{ marginBottom: "var(--nqh-space-2)" }}>
          <h2 className={styles.modalTitle} id="sc011AuditTitle">
            Lịch sử xử lý
          </h2>
          <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        {items.length === 0 ? (
          <p style={{ margin: 0, color: "var(--nqh-gray-500, #6b7280)", fontSize: 14 }}>
            Chưa có thao tác xử lý nào cho bản ghi này.
          </p>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 12 }}>
            {items.map((it) => (
              <li key={`${it.label}-${it.at}`} style={{ display: "grid", gap: 2 }}>
                <strong style={{ fontSize: 14 }}>{it.label}</strong>
                <span style={{ fontSize: 13, color: "var(--nqh-gray-600, #4b5563)" }}>
                  {new Date(it.at).toLocaleString("vi-VN")}
                  {it.actor ? ` · ${it.actor}` : ""}
                </span>
                {it.detail && (
                  <span style={{ fontSize: 13, color: "var(--nqh-gray-700, #374151)", fontStyle: "italic" }}>
                    "{it.detail}"
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </PortalMenu>
  );
};

/** ISS-MGR-22 §98 điểm 5 — sentinel riêng cho "chưa phân khu vực"
 * (`work_area_id===null`), KHÔNG dùng chuỗi rỗng nữa (chuỗi rỗng trước đây
 * TRÙNG key với "Tất cả" = không lọc gì → 2 lựa chọn khác nghĩa nhưng chung
 * 1 key). "" nay CHỈ còn nghĩa "không lọc" (tập committed rỗng). */
export const UNASSIGNED_WORK_AREA = "__unassigned__";

// ── Nhóm dòng cho bảng Gộp/Phẳng (+ heading Ngày khi đang xem khoảng) ──────

interface RowGroup {
  key: string;
  rows: TeamAttendanceRow[];
}
interface TableSection {
  dateLabel: string | null;
  groups: RowGroup[];
}

export function buildSections(rows: TeamAttendanceRow[], viewMode: "grouped" | "flat", isRangeMode: boolean): TableSection[] {
  // bot-047 (#829, 2026-09-12): heading Ngày trước đây "mới nhất trước" (giảm dần) — ngược chiều
  // đọc của chính nút filter khoảng ngày ("Từ ngày – Đến ngày", trái→phải = cũ→mới), khiến Quản lý
  // đọc nhầm thứ tự thời gian khi so sánh tiến trình chấm công qua nhiều ngày. Đổi sang tăng dần
  // (cũ→mới) để khớp chiều đọc của filter — theo yêu cầu trực tiếp, CHƯA đồng bộ ngược lại
  // WIREFRAME-V0-MASTER-REGISTRY.md (2 dòng còn ghi "mới nhất trước") — nợ spec-sync, xử lý sau.
  const ordered = isRangeMode ? [...rows].sort((a, b) => a.date.localeCompare(b.date)) : rows;
  const dateBuckets: { dateLabel: string | null; rows: TeamAttendanceRow[] }[] = [];
  if (isRangeMode) {
    const seen = new Map<string, TeamAttendanceRow[]>();
    const order: string[] = [];
    ordered.forEach((r) => {
      if (!seen.has(r.date)) {
        seen.set(r.date, []);
        order.push(r.date);
      }
      seen.get(r.date)!.push(r);
    });
    order.forEach((date) => dateBuckets.push({ dateLabel: date, rows: seen.get(date)! }));
  } else {
    dateBuckets.push({ dateLabel: null, rows: ordered });
  }
  return dateBuckets.map((bucket) => {
    if (viewMode === "flat") {
      return {
        dateLabel: bucket.dateLabel,
        groups: bucket.rows.map((r) => ({ key: r.attendance_ref ?? `${r.schedule_id}-${r.user_id}`, rows: [r] })),
      };
    }
    const groups: RowGroup[] = [];
    const byUser = new Map<string, RowGroup>();
    bucket.rows.forEach((r) => {
      let g = byUser.get(r.user_id);
      if (!g) {
        g = { key: r.user_id, rows: [] };
        byUser.set(r.user_id, g);
        groups.push(g);
      }
      g.rows.push(r);
    });
    return { dateLabel: bucket.dateLabel, groups };
  });
}

export const sectionRowCount = (sections: TableSection[]) => sections.reduce((sum, s) => sum + s.groups.reduce((n, g) => n + g.rows.length, 0), 0);
export const sectionGroupCount = (sections: TableSection[]) => sections.reduce((sum, s) => sum + s.groups.length, 0);

/** Issue #712 (BOT-023) — chip "N cần xử lý" [6]: quyết định có mở rộng
 * khoảng ngày sang cửa sổ trailing 92 ngày hay không khi bật lọc pending.
 * Tách thành hàm thuần (pure) để test không cần render toàn bộ trang (page
 * phụ thuộc TanStack Query + nhiều hook khác, chưa có harness mock trong
 * repo — extract giữ test rẻ + đúng, không kéo theo rewrite ngoài phạm vi).
 * Bug cũ: so sánh `currentlyVisiblePending === 0` bỏ sót case còn thiếu MỘT
 * PHẦN (vd 2 dòng đang hiện / 3 dòng pending server-side) — dùng `<` để bắt
 * cả 2 case "thiếu tất cả" lẫn "thiếu một phần". */
export const shouldExpandPendingWindow = (currentlyVisiblePending: number, pendingCount: number): boolean =>
  currentlyVisiblePending < pendingCount;

/** Wireframe SC-011 [6] amend 2026-09-07 (@ba, UAT thật): quyết định [3] khi
 * TẮT chip. `jumpedForPending` = lượt BẬT gần nhất có tự nhảy khoảng ngày hay
 * không (xem `shouldExpandPendingWindow`). Trả về date filter MỚI nếu cần đổi
 * (về "Hôm nay"), hoặc `null` nếu không có gì để hoàn tác (giữ nguyên [3] —
 * đúng quyết định gốc 2026-08-28 cho case lượt bật không cần nhảy). Tách hàm
 * thuần cùng lý do với `shouldExpandPendingWindow` — test không cần render. */
export const dateFilterAfterChipToggleOff = (
  jumpedForPending: boolean,
  todayISO: string,
): DateFilterValue | null => (jumpedForPending ? { mode: "single", date: todayISO } : null);

// ── Component ──────────────────────────────────────────────────────────────

const ManagerAttendancePage: React.FC = () => {
  const todayISO = toISODate(new Date());

  // [3] date filter — Luật 13 (single ngày mặc định = hôm nay)
  const [dateFilter, setDateFilter] = useState<DateFilterValue>({ mode: "single", date: todayISO });
  // [1] Địa điểm — Quản lý 1 cơ sở → khoá cứng (registry), hiện dạng badge tĩnh
  // (giống ISS-MGR-20 cho "Đơn vị" ở bản cũ — không có endpoint đổi chi nhánh
  // của caller). [2] Khu vực làm việc — ISS-MGR-22 (§98 "Yêu cầu MỚI"): đổi
  // sang multi-select + apply-on-close. "Committed" = tập ĐANG thực sự lọc
  // (dùng trong `flatRows` bên dưới); "pending" = tập đang tick DỞ trong
  // panel, chưa áp dụng — 2 state tách biệt để tick không gọi API ngay.
  // Quyết định 2 (chốt sẵn): lọc CLIENT-SIDE trên field `work_area_id` có sẵn
  // từng dòng response — KHÔNG gửi `work_area_id` lên backend nữa (param đó
  // vẫn là scalar; đổi sang mảng phải đợi bug F2 vá, BS-200 §8).
  const [workAreaIdsCommitted, setWorkAreaIdsCommitted] = useState<string[]>([]);
  const [workAreaIdsPending, setWorkAreaIdsPending] = useState<string[]>([]);
  // [1b] Tìm nhân viên (multi-select) — ISS-MGR-22: đã multi-select sẵn,
  // NHƯNG trigger sai thời điểm (mỗi tick → gọi API ngay). Cùng pattern
  // committed/pending với Khu vực làm việc ở trên: `employeeIdsCommitted` là
  // tập THỰC SỰ dùng trong `listParams`/`useTeamAttendance()`; `employeeIdsPending`
  // chỉ đổi để checkbox hiện đúng trạng thái tick tức thời trong panel.
  const [employeeIdsCommitted, setEmployeeIdsCommitted] = useState<string[]>([]);
  const [employeeIdsPending, setEmployeeIdsPending] = useState<string[]>([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const debouncedEmployeeSearch = useDebounce(employeeSearch, 300);
  const [employeePanelOpen, setEmployeePanelOpen] = useState(false);
  // 2026-09-19 (fix @coder, bug thật do @tester phát hiện qua ảnh chụp): panel
  // "Tìm nhân viên" (và menu "Khu vực làm việc" ngay dưới) trước dùng
  // `.menuAnchor{position:relative}`+`.menu{position:absolute}` — bị `.filterBar`
  // (đổi `overflow-x:auto`/`overflow-y:hidden` ngày 2026-09-16, yêu cầu "đưa
  // toolbar lên cùng 1 hàng như Phân ca") CẮT ĐỨT, y hệt bug đã gặp + đã vá ở
  // `ManagerSchedulePage.tsx` (2026-09-14, xem comment đầu file đó). Đổi sang
  // `PortalMenu` (portal + position:fixed, thoát mọi ancestor overflow) — cùng
  // kỹ thuật `AuditLogPopup` trong chính file này đã dùng. `employeePanelRef`/
  // `areaMenuRef` (đo rect qua ref) không còn cần — đo trực tiếp qua
  // `e.currentTarget.getBoundingClientRect()` lúc bấm, khớp cách
  // ManagerSchedulePage đã làm.
  const [employeeMenuAnchor, setEmployeeMenuAnchor] = useState<PortalMenuAnchor | null>(null);
  const [areaMenuAnchor, setAreaMenuAnchorPos] = useState<PortalMenuAnchor | null>(null);
  // [8] Gộp/Phẳng
  const [viewMode, setViewMode] = useState<"grouped" | "flat">("grouped");
  // BOT-050 (#839, 2026-09-14) — filter "Trạng thái", backend `status_filter`
  // (alias `status`) đã có sẵn (manager_attendance.py L1004+), chỉ thiếu
  // wiring FE. "" = không lọc (Tất cả trạng thái). Lọc SERVER-SIDE (khác
  // "Khu vực làm việc" ở trên — filter đó CỐ Ý client-side, xem Quyết định 2)
  // vì `status` là param đã tồn tại sẵn trong `listParams`/query key, không
  // cần cơ chế committed/pending riêng — 1 lựa chọn = 1 lần gọi API.
  const [statusFilter, setStatusFilter] = useState<string>("");
  // [6] chip "N cần xử lý"
  const [pendingFilterOn, setPendingFilterOn] = useState(false);
  // Wireframe SC-011 [6] amend 2026-09-07 (@ba, UAT thật): nhớ lượt BẬT chip
  // gần nhất có tự nhảy khoảng ngày hay không, để lượt TẮT biết có nên đưa
  // [3] về "Hôm nay" hay giữ nguyên. Không cần re-render khi đổi → useRef.
  const jumpedForPendingRef = useRef(false);
  // [9b] Xuất ▼
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [areaMenuOpen, setAreaMenuOpen] = useState(false);

  // STATE READ-ONLY (wireframe SC-011, MA TRẬN PHÂN QUYỀN) — vai Chủ doanh
  // nghiệp (TENANT_OWNER) chỉ ĐƯỢC XEM, không được Xác nhận công/Gắn cờ/Đăng
  // ký ngoài giờ. 2026-09-07 (vòng 2 sync wireframe): backend đã khoá bằng
  // `require_attendance_write()` — đây là phần FE tương ứng (ẩn chip [6],
  // banner nhẹ, khoá nút Xác nhận công). KHÔNG chặn "Vào màn" (wireframe ghi
  // "⏳" chưa chốt hẳn cho cột đó, khác "❌" — giữ nguyên hành vi vào màn).
  const { userRole } = useAuthStore();
  const isOwnerReadOnly = (userRole ?? "").toUpperCase() === "TENANT_OWNER";

  // STATE OFFLINE (wireframe SC-011) — banner "Mất kết nối" + khoá [17]/Xuất
  // cho tới khi mạng trở lại. Thuần client-side (navigator.onLine), không cần
  // endpoint riêng.
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const [toast, setToast] = useState<string | null>(null);

  const isRangeMode = dateFilter.mode === "range";
  const dateParams: TeamAttendanceParams =
    dateFilter.mode === "single" ? { date: dateFilter.date } : { date_from: dateFilter.from, date_to: dateFilter.to };

  const listParams: TeamAttendanceParams = {
    ...dateParams,
    // ISS-MGR-22 Quyết định 2: "Khu vực làm việc" lọc CLIENT-SIDE (xem
    // `flatRows` bên dưới) — KHÔNG gửi `work_area_id` lên backend nữa.
    ...(employeeIdsCommitted.length ? { employee_ids: employeeIdsCommitted } : {}),
    // BOT-050 (#839) — "Trạng thái" lọc SERVER-SIDE qua `status_filter` có sẵn.
    ...(statusFilter ? { status: statusFilter } : {}),
  };
  const listQuery = useTeamAttendance(listParams);
  const staffQuery = useManagerStaff();
  const resolveMutation = useResolveAttendance();
  const bulkResolveMutation = useBulkResolveAttendance();

  const data = listQuery.data;
  const branchLabel = data?.branch_code || data?.branch_name || "";

  // ISS-MGR-22 Quyết định 2: lọc "Khu vực làm việc" CLIENT-SIDE ở đây, trên
  // field `work_area_id` có sẵn TỪNG DÒNG response. BS-011 R27-R30 (2026-08-18,
  // F2 vá ở backend): `work_area_id` nay neo đúng `Schedule.organization_id`
  // (không còn luôn null như trước khi vá — xem manager_attendance.py
  // _resolve_area_label()), nên filter này giờ mới thực sự lọc được gì đó.
  const flatRows = useMemo(() => {
    const rows = (data?.groups ?? []).flatMap((g) => g.rows);
    if (!workAreaIdsCommitted.length) return rows;
    return rows.filter((r) => workAreaIdsCommitted.includes(r.work_area_id ?? UNASSIGNED_WORK_AREA));
  }, [data, workAreaIdsCommitted]);

  // ISS-MGR-22 §98 "Yêu cầu MỚI" (2026-08-18) — "Khu vực làm việc" đổi sang
  // HYBRID 2 nguồn, hợp nhất (merge, dedupe theo `id`, thứ tự không quan
  // trọng — chỉ cấm trùng `id`):
  //   1) "Giá trị mặc định" — khu vực work_area THẬT của tenant/scope, ỔN
  //      ĐỊNH, KHÔNG phụ thuộc có log/bản ghi chấm công hay không (xem
  //      `defaultAreaOptions` ngay dưới).
  //   2) "Giá trị mới phát hiện từ log" — `areaScanQuery` (đã có sẵn, GIỮ
  //      NGUYÊN — xem block comment REGRESSION FIX ngay dưới nó): quét 92
  //      ngày log Attendance/Schedule THẬT, suy ra CẢ nhãn ĐỘNG "Nhân viên
  //      {tên org}"/"Nhân viên {tên tenant}" mà roster không có (NV neo cấp
  //      KHÁC work_area — branch/vùng/...).
  //
  // Nguồn (1): TÁI DÙNG `staffQuery.data.items` — CÙNG roster đã dùng cho
  // "Tìm nhân viên" [1b] phía trên (không gọi thêm API).
  //
  // ISS-MGR-22 (2026-08-19, §98 bug thật): trước đây dòng này lọc CHỈ
  // `work_area_id !== null` (khu vực work_area THẬT theo SC-012) — NV neo
  // NGOÀI cấp work_area (branch/region/...) bị loại HOÀN TOÀN khỏi nguồn
  // này. Nếu tenant/branch đó CHƯA TỪNG có log Attendance/Schedule (nguồn
  // (2) `areaScanQuery` bên dưới cũng "mù" — quét log, không có log thì
  // không suy ra được gì), khu vực đó BIẾN MẤT khỏi dropdown hoàn toàn (case
  // thật: manager neo thẳng branch "The Kupid Homestay", tenant 0 dòng
  // schedules/attendances).
  //
  // Sửa: đổi nguồn sang `raw_organization_id`/`raw_organization_name` (field
  // ADDITIVE mới ở backend `manager_staff.py` — TÁCH BIỆT hoàn toàn khỏi
  // `work_area_id`/`work_area_name`, KHÔNG đụng SC-012/R30 guardrail). 2
  // field mới LUÔN có giá trị cho MỌI nhân viên trong roster, bất kể neo ở
  // cấp nào — nhãn ĐỘNG theo `_resolve_area_label()` (R27/R28 pattern, cùng
  // công thức màn Chấm công đã dùng cho bảng chính): cấp work_area → tên
  // khu vực thật; cấp khác → "Nhân viên {tên node}"; không resolve được →
  // "Nhân viên {tên tenant}". `raw_organization_id` chỉ null khi
  // `user.organization_id` chính nó là None — dùng sentinel
  // `UNASSIGNED_WORK_AREA` cho case đó để nhất quán với merge nguồn (2).
  const defaultAreaOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    (staffQuery.data?.items ?? []).forEach((it) => {
      const id = it.raw_organization_id ?? UNASSIGNED_WORK_AREA;
      if (!map.has(id)) map.set(id, { id, name: it.raw_organization_name ?? it.work_area_name });
    });
    return [...map.values()];
  }, [staffQuery.data]);

  // REGRESSION FIX (2026-08-18, phát hiện qua test thủ công): trước đây
  // "Khu vực làm việc" đọc thẳng từ `listQuery.data` (bảng chính, theo ĐÚNG
  // ngày/khoảng đang xem) — nếu ngày/khoảng đó không có bản ghi nào (vd
  // trang mặc định mở = hôm nay, thường 0 dòng), `data.groups` rỗng →
  // dropdown chỉ còn "Tất cả" (hardcode), MẤT hết lựa chọn thật dù các khu
  // vực đó có tồn tại ở ngày khác. Sai vì danh sách lựa chọn LỌC phải liệt
  // kê ĐẦY ĐỦ khu vực có thể lọc, KHÔNG phụ thuộc ngày/khoảng đang xem.
  //
  // Sửa: 1 query TanStack Query RIÊNG (`areaScanQuery`, cùng hook
  // `useTeamAttendance`) quét khoảng ngày RỘNG NHẤT backend cho phép — 92
  // ngày (`MAX_ATTENDANCE_RANGE_DAYS`, xem `manager_attendance.py`), CÙNG
  // cửa sổ trailing đã dùng cho `toggleChip` phía trên (§6 API contract) —
  // KHÔNG lọc theo `employee_ids`/khu vực, CHỈ để suy ra danh sách khu vực.
  // Query này KHÔNG dùng để hiển thị bảng chính — bảng chính vẫn đúng
  // `listParams`/`listQuery`/`flatRows` theo ngày người dùng đang chọn, không
  // đổi gì ở đó. `staleTime` 5 phút (thay vì mặc định 30s của bảng chính) vì
  // đây là dữ liệu phụ trợ (danh sách lựa chọn lọc), không cần realtime.
  const areaScanParams: TeamAttendanceParams = { date_from: addDays(todayISO, -91), date_to: todayISO };
  const areaScanQuery = useTeamAttendance(areaScanParams, { staleTime: 5 * 60_000 });

  // Hợp nhất 2 nguồn — (1) mặc định TRƯỚC, (2) log-scan SAU, Map dedupe theo
  // `id` (entry tới trước giữ nguyên nếu trùng `id`, không xảy ra thực tế vì
  // 2 nguồn cùng anchor `Organization.id` nên tên khớp nhau khi trùng).
  const areaOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    defaultAreaOptions.forEach((a) => map.set(a.id, a));
    (areaScanQuery.data?.groups ?? []).forEach((g) => {
      const id = g.work_area_id ?? UNASSIGNED_WORK_AREA;
      if (!map.has(id)) map.set(id, { id, name: g.work_area_name });
    });
    return [...map.values()];
  }, [defaultAreaOptions, areaScanQuery.data]);

  // ISS-MGR-22 (2026-08-19, bug thật thứ 2): filter panel "Tìm nhân viên"
  // PHẢI so khớp CÙNG không gian ID với dropdown "Khu vực làm việc" phía
  // trên — `workAreaIdsCommitted` giờ chứa giá trị từ `defaultAreaOptions`
  // (nguồn `raw_organization_id`, xem block comment ở đó), KHÔNG còn phải
  // `work_area_id` cũ. Trước đây dòng dưới vẫn so theo `it.work_area_id`
  // (field CŨ, null cho NV neo ngoài cấp work_area) → chọn 1 khu vực MỚI
  // (chỉ có trong `raw_organization_id`, vd "Nhân viên The Kupid Homestay")
  // sẽ không khớp gì → panel lọc rỗng SAI. `raw_organization_id` là tập MỞ
  // RỘNG của `work_area_id` (cùng giá trị khi NV đúng cấp work_area, thêm
  // giá trị cho NV neo cấp khác) — đổi field không phá case cũ.
  const employeeOptionsInScope = useMemo(() => {
    let items = staffQuery.data?.items ?? [];
    if (workAreaIdsCommitted.length) {
      items = items.filter((it) => workAreaIdsCommitted.includes(it.raw_organization_id ?? UNASSIGNED_WORK_AREA));
    }
    return items;
  }, [staffQuery.data, workAreaIdsCommitted]);

  const filteredEmployeeOptions = useMemo(() => {
    // BUG THẬT (2026-09-08, user báo lại lần 2 — "hà quốc" không tìm thấy
    // "Hà Quốc Bảo" dù gõ đúng, kể cả sau ctrl+shift+R): KHÔNG phải do cache
    // — `.toLowerCase().includes()` so sánh CODE POINT trần, nhưng chuỗi có
    // dấu tiếng Việt có 2 dạng Unicode tương đương nhưng KHÁC byte: NFC (1
    // ký tự tổ hợp sẵn, "à" = U+00E0) vs NFD (chữ cái + dấu rời, "à" = "a"
    // U+0061 + U+0300). Roster lưu 1 dạng (thường NFC, từ DB/seed script),
    // bàn phím/IME hoặc clipboard nguồn (vd copy từ macOS) có thể gõ ra dạng
    // KIA — 2 chuỗi hiển thị GIỐNG HỆT nhau trên màn hình nhưng
    // `"Hà".includes("hà")` trả `false` nếu lệch dạng chuẩn hoá. `.normalize
    // ("NFC")` ép cả 2 vế về CÙNG 1 dạng trước khi so — an toàn/idempotent
    // với chuỗi đã NFC sẵn, không đổi hành vi "không bỏ dấu" (registry khối
    // [1b]) — chỉ gộp 2 encoding của CÙNG 1 ký tự, không gộp 2 ký tự khác nhau.
    // BOT-046 (#827, 2026-09-14): BUG THẬT — gõ 2+ khoảng trắng liên tiếp Ở
    // GIỮA tên (vd "Nguyễn  Văn A", double-space do gõ nhanh/paste) khiến
    // `q` giữ nguyên khoảng trắng kép trong khi `full_name` roster chỉ có 1
    // khoảng trắng → `.includes(q)` sai lệch từng ký tự, filter báo "không
    // tìm thấy" dù đúng người. `.replace(/\s+/g, " ")` gộp MỌI dải khoảng
    // trắng liên tiếp (đầu/giữa/cuối) về đúng 1 dấu cách — áp dụng CẢ 2 vế so
    // sánh (input người gõ LẪN full_name roster) để nhất quán, phòng dữ liệu
    // roster tự nó cũng lỡ có khoảng trắng kép (nhập tay/import lỗi).
    const q = debouncedEmployeeSearch.trim().replace(/\s+/g, " ").toLowerCase().normalize("NFC");
    if (!q) return employeeOptionsInScope;
    // Substring, KHÔNG phân biệt hoa/thường, KHÔNG bỏ dấu — đúng giới hạn
    // production thật (registry khối [1b]).
    return employeeOptionsInScope.filter((it) =>
      it.full_name.replace(/\s+/g, " ").toLowerCase().normalize("NFC").includes(q),
    );
  }, [employeeOptionsInScope, debouncedEmployeeSearch]);

  // Toast tự ẩn sau 4s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Đóng menu "Xuất ▼" khi click ra ngoài — nút này nằm NGOÀI `.filterBar`
  // (khối header trên) nên KHÔNG bị bug cắt-đứt 2026-09-16 nói trên, vẫn giữ
  // nguyên cơ chế cũ (`.menuAnchor` của nó tự `onMouseDown` stopPropagation
  // cho phần TRONG nó nên không cần contains-check ở đây).
  // 2026-09-19: bỏ 2 nhánh employeePanelOpen/areaMenuOpen khỏi effect này —
  // đóng + commit pending→committed của 2 bộ lọc đó giờ tự xử lý qua
  // `PortalMenu.onClose` (xem JSX bên dưới), không cần listener chung nữa.
  useEffect(() => {
    if (!exportMenuOpen) return;
    const close = () => setExportMenuOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [exportMenuOpen]);

  const visibleRows = useMemo(() => (pendingFilterOn ? flatRows.filter(isPendingRow) : flatRows), [flatRows, pendingFilterOn]);
  const sections = useMemo(() => buildSections(visibleRows, viewMode, isRangeMode), [visibleRows, viewMode, isRangeMode]);
  // Khi lọc chỉ-còn-pending (`pendingFilterOn`), MỌI dòng trong `visibleRows`
  // đều pending → `canMergeActionCell` luôn false (loại pending khỏi gộp) →
  // Gộp/Phẳng render Y HỆT NHAU (không ô nào rowSpan, mỗi bản ghi 1 `<tr>`
  // riêng, dù cùng nhân viên/ngày). "X dòng" theo định nghĩa NHÓM
  // (`sectionGroupCount`) lúc này đếm SAI so với số `<tr>` thật đang hiện —
  // đúng gốc rễ mismatch "46 cần xử lý" vs "30 dòng" mà user chỉ ra (2 bản
  // ghi pending cùng nhân viên/ngày gộp thành 1 "dòng" dù bảng vẫn hiện 2
  // hàng riêng). Dùng `sectionRowCount` (số bản ghi thật) bất kể `viewMode`
  // trong trạng thái này — khớp đúng số hàng mắt thấy.
  const rowCountLabel = pendingFilterOn || viewMode === "flat" ? sectionRowCount(sections) : sectionGroupCount(sections);

  // ── Checkbox cột "Thao tác" + thanh bulk "N đã chọn" ──
  // Chỉ dòng hàng-chờ-xử-lý (`isPendingRow`) có checkbox (đúng prototype
  // `isPendingRec` — hàm `renderActionCellInner` cũng chỉ mở modal ghi cho
  // đúng nhóm này). OWNER read-only không được GHI nên không có gì để chọn —
  // đồng nhất với khoá ở `renderActionCellInner`/nút đơn.
  const selectableRows = useMemo(
    () => (isOwnerReadOnly ? [] : visibleRows.filter(isPendingRow)),
    [visibleRows, isOwnerReadOnly],
  );
  const selectableKeys = useMemo(
    () => new Set(selectableRows.map((r) => r.attendance_ref).filter((ref): ref is string => !!ref)),
    [selectableRows],
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Dữ liệu đổi (refetch/đổi bộ lọc/1 dòng vừa được xử lý xong) → bỏ khỏi
  // selection những ref không còn ∈ danh sách hàng-chờ-xử-lý hiện tại, tránh
  // gửi bulk cho 1 bản ghi đã đổi trạng thái từ dưới tay.
  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => selectableKeys.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectableKeys]);
  const selectedCount = selectedIds.size;
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => r.attendance_ref && selectedIds.has(r.attendance_ref));
  const someSelected = selectedCount > 0 && !allSelected;
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(selectableKeys));
  const toggleRowSelect = (ref: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  const [bulkConfirmAction, setBulkConfirmAction] = useState<"record" | "defer" | null>(null);
  const [bulkReason, setBulkReason] = useState("");
  const selectedRows = useMemo(
    () => visibleRows.filter((r) => r.attendance_ref && selectedIds.has(r.attendance_ref)),
    [visibleRows, selectedIds],
  );

  // pendingCount (bản ghi) — chip [6] LUÔN đếm theo SỐ BẢN GHI ở CẢ HAI chế
  // độ Gộp/Phẳng (phân xử Product Owner 2026-09-04, ghi trong
  // `sc011-build-prototype.html` hàm `pendingCount()` dòng ~731-736): Gộp/
  // Phẳng chỉ là cách HIỂN THỊ, khối lượng việc cần xử lý không đổi — 1 nhân
  // viên có 2 bản ghi pending vẫn là 2 lần mở modal, gộp lại đếm thành 1 là
  // báo THIẾU việc. Trước đây đổi sang `pending_count_grouped` khi ở Gộp —
  // sai theo đúng phân xử này (registry §2 SC-011 khối [FILTER [6]] ghi
  // "đếm theo NHÓM" là phần CHƯA amend theo phân xử, không phải nguồn đúng).
  const pendingCount = data?.pending_count ?? 0;
  const pendingCountDisplay = pendingCount;

  const toggleEmployee = (userId: string) => {
    setEmployeeIdsPending((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  };
  const toggleWorkArea = (id: string) => {
    setWorkAreaIdsPending((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleChip = () => {
    if (!pendingFilterOn) {
      const currentlyVisiblePending = flatRows.filter(isPendingRow).length;
      // BOT-023 fix (Issue #712): so sánh cũ `currentlyVisiblePending === 0`
      // chỉ mở rộng khoảng ngày khi khung hiện tại KHÔNG hiện dòng pending
      // nào — nếu đã hiện MỘT VÀI dòng (vd 2/3) nhưng vẫn thiếu so với
      // `pendingCount` server-side, điều kiện cũ không kích hoạt → mất
      // record chưa hiển thị (rủi ro bỏ sót chấm công ảnh hưởng lương/OT).
      // `shouldExpandPendingWindow` dùng `<` để bắt luôn case "còn thiếu một
      // phần", không chỉ case "thiếu tất cả" — xem test đi kèm hàm đó.
      const willJump = shouldExpandPendingWindow(currentlyVisiblePending, pendingCount);
      jumpedForPendingRef.current = willJump;
      // [3] không chứa đủ dòng cần xử lý → nhảy sang ĐÚNG khoảng chứa hết.
      // Nhảy về `oldest_pending_date` — ngày sớm nhất tính TRỰC TIẾP từ chính
      // tập bản ghi đang pending (backend `_count_pending_in_scope`) — KHÔNG
      // dùng `tenant_created_at` (mốc tạo tenant): "tenant không có dữ liệu
      // trước ngày tạo" không được ràng buộc ở tầng dữ liệu, và đã ĐO ĐƯỢC vi
      // phạm thật (2026-09-12 — 6 bản ghi pending có ngày sớm hơn
      // tenant_created_at, chip báo 46 nhưng nhảy tới tenant_created_at chỉ
      // thấy 40 dòng). `oldest_pending_date` luôn non-null ở đây vì `willJump`
      // chỉ true khi `pendingCount` > 0.
      if (willJump && data?.oldest_pending_date) {
        setDateFilter({ mode: "range", from: data.oldest_pending_date, to: todayISO });
      }
    } else {
      const nextFilter = dateFilterAfterChipToggleOff(jumpedForPendingRef.current, todayISO);
      if (nextFilter) setDateFilter(nextFilter);
      jumpedForPendingRef.current = false;
    }
    setPendingFilterOn((v) => !v);
  };

  // ── [18]/[18-RO] modal + [19] dialog state ──

  const [modalRow, setModalRow] = useState<TeamAttendanceRow | null>(null);
  // 2026-09-07 (sync wireframe [18-LOG]): popup "Lịch sử xử lý" phải là popup
  // NHỎ neo theo nút 🕐 (KHÔNG dim/đóng modal cha phía sau) — lưu rect của nút
  // lúc bấm để định vị qua PortalMenu (cùng kỹ thuật portal đã dùng cho
  // PortalTooltip/PortalMenu ở nơi khác), thay vì tái dùng `.modalOverlay`
  // full-dim/center vốn dành cho modal thật.
  const [auditLogAnchor, setAuditLogAnchor] = useState<PortalMenuAnchor | null>(null);
  // 2026-09-07 (sync wireframe [18]/[18-RO] khối "Ảnh chấm công"): hook + API
  // ĐÃ CÓ SẴN từ trước (`useAttendancePhoto`/`managerApi.getAttendancePhoto`)
  // nhưng chưa từng được gọi ở trang này — khối "Ảnh chấm công" chỉ render 1
  // icon + chữ, không có ảnh thật nào. Chỉ fetch khi modal đang mở VÀ record
  // có `attendance_id` thật (đúng thiết kế sẵn của hook — không tải ảnh cho
  // record "Thiếu chấm công" vốn không có ảnh nào để tải).
  const attendancePhotoQuery = useAttendancePhoto(modalRow?.attendance_id ?? null);
  const [showBranchConfig, setShowBranchConfig] = useState(false);
  // [10]⚙ Quyền tự cấu hình theo cơ sở — phải BIẾT TRƯỚC khi render, vì registry
  // §2 SC-011 `[10]` yêu cầu nút "ẨN HẲN theo mặc định (không hiện dạng
  // mờ/disabled)". Lỗi tải → giữ null → nút vẫn ẩn (fail-closed: thà thiếu nút
  // còn hơn hiện nút bấm vào chỉ để nhận 403).
  const [branchCfg, setBranchCfg] = useState<BranchWorkforceConfig | null>(null);

  useEffect(() => {
    let alive = true;
    managerApi
      .getBranchWorkforceConfig()
      .then((d) => alive && setBranchCfg(d))
      .catch(() => alive && setBranchCfg(null));
    return () => {
      alive = false;
    };
  }, []);
  const canSelfConfigure = Boolean(branchCfg && (branchCfg.editable.tolerance || branchCfg.editable.auto_record));
  const [editMode, setEditMode] = useState(false);
  const [computedInDraft, setComputedInDraft] = useState("");
  const [computedOutDraft, setComputedOutDraft] = useState("");
  const [managerNoteDraft, setManagerNoteDraft] = useState("");
  const [matchedCode, setMatchedCode] = useState<string>(""); // "" = Không khớp ca nào
  // Khối [18e] — bảng sửa-rồi-lưu: giữ nguyên cụm trong state, gửi 1 lần khi
  // bấm "Ghi nhận công" (không có API riêng cho từng dòng).
  const [exceptionsDraft, setExceptionsDraft] = useState<AttendanceExceptionLine[]>([]);
  const [excMenuOpen, setExcMenuOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"record" | "defer" | "reject_close" | null>(null);
  // Quyết định "Điều chỉnh"/"Bỏ qua" theo TỪNG nhãn lệch giờ — bắt buộc chọn
  // đủ trước khi ghi nhận công thật (popover riêng chặn ở `pendingDecisionOpen`
  // nếu Quản lý bấm "Ghi nhận công" khi còn nhãn chưa quyết định).
  const [labelDecisions, setLabelDecisions] = useState<Record<string, "adjust" | "ignore">>({});
  const [pendingDecisionOpen, setPendingDecisionOpen] = useState(false);
  // [Tra cứu luật gán nhãn] — component riêng ở CUỐI TRANG, độc lập với modal
  // [18]/[18-RO]: Quản lý chọn bất kỳ bản ghi nào đang hiển thị để xem luật
  // đứng sau từng nhãn Trạng thái của bản ghi đó, không cần mở modal chi tiết.
  const [ruleLookupKey, setRuleLookupKey] = useState("");

  const shiftCodesQuery = useScheduleShiftCodes();

  // Chế độ modal đi theo THAO TÁC của dòng ([17]), không theo nhãn Trạng thái:
  // dòng còn "Xác nhận công" (pending_review) → modal [18] Xử lý, dòng đã chốt
  // → [18-RO] Xem. Dòng "Thiếu chấm công" cũng nằm trong hàng chờ nên xử lý
  // được như mọi dòng chờ khác (registry SC-011 LAYOUT — ví dụ Đỗ Thị Mai).
  const isProcessMode = !!modalRow && (modalRow.action_state === "pending_review" || editMode);

  // Bản ghi ĐÃ có ≥1 dòng "Điều chỉnh" từ lần lưu trước → coi như mọi nhãn
  // lệch giờ đã được xử lý (không thể map ngược 1 dòng ↔ đúng 1 nhãn khi
  // Quản lý gộp chung ghi chú nhiều nhãn vào 1 dòng, như "Đi trễ + Về sớm").
  // 0 dòng "Điều chỉnh" → chưa ai xử lý, để trống buộc chọn lại từ đầu.
  const seedLabelDecisions = (row: TeamAttendanceRow): Record<string, "adjust" | "ignore"> => {
    if (!row.exceptions.some((e) => e.kind === "adjustment")) return {};
    const decisions: Record<string, "adjust" | "ignore"> = {};
    for (const code of row.status_labels) {
      if (DEVIATION_STATUS_CODES.has(code)) decisions[code] = "adjust";
    }
    return decisions;
  };
  const openModal = (row: TeamAttendanceRow) => {
    setModalRow(row);
    setEditMode(false);
    setComputedInDraft(timeOfISO(row.computed_check_in));
    setComputedOutDraft(timeOfISO(row.computed_check_out));
    setManagerNoteDraft(row.manager_notes ?? "");
    setMatchedCode(row.is_matched ? row.shift_code ?? "" : "");
    setExceptionsDraft(row.exceptions.map((e) => ({ ...e })));
    setExcMenuOpen(false);
    setLabelDecisions(seedLabelDecisions(row));
    setPendingDecisionOpen(false);
  };
  const closeModal = () => {
    setModalRow(null);
    setConfirmAction(null);
  };
  // "Sửa lại" trên 1 record ĐÃ CHỐT (recorded/not_recorded) mở khoá field
  // NGAY TRONG modal đang mở (đúng thiết kế) nhưng nếu Quản lý không đổi gì
  // rồi muốn thoát, footer chỉ có "Hoãn ghi nhận"/"Ghi nhận công" — cả 2 đều
  // là HÀNH ĐỘNG THẬT (Hoãn đẩy record ĐÃ ghi nhận NGƯỢC về hàng chờ xử lý,
  // không phải "huỷ xem"). Khác record CÒN pending_review (chưa từng quyết
  // định gì) — ở đó "Hoãn ghi nhận" đúng là lối thoát trung tính theo thiết
  // kế. `cancelEdit` chỉ áp dụng cho case đầu, và thoát THẲNG RA màn "Chấm
  // công" (không gọi API, không đổi action_state) — trước đây trả về [18-RO]
  // trong CÙNG modal, bắt Quản lý bấm thêm "Đóng" để xem lại y hệt thông tin
  // vừa thấy trước khi "Sửa lại" (BUG THẬT: 2026-09-08, user báo qua ảnh chụp
  // UI — vòng lặp thừa không có tác dụng gì).
  const cancelEdit = () => {
    closeModal();
  };

  const matchedShiftMeta = useMemo(() => {
    const items = shiftCodesQuery.data?.items ?? [];
    if (matchedCode) return items.find((i) => i.code === matchedCode) ?? null;
    return null;
  }, [shiftCodesQuery.data, matchedCode]);

  const presetByShift = () => {
    if (!modalRow) return;
    const start = matchedShiftMeta ? matchedShiftMeta.start_time : modalRow.scheduled_start;
    const end = matchedShiftMeta ? matchedShiftMeta.end_time : modalRow.scheduled_end;
    if (!start || start === "—" || !end || end === "—") return;
    setComputedInDraft(start.slice(0, 5));
    setComputedOutDraft(end.slice(0, 5));
  };
  const presetByActual = () => {
    if (!modalRow?.clock_in) return;
    setComputedInDraft(timeOfISO(roundToHalfHourISO(modalRow.clock_in)));
    setComputedOutDraft(modalRow.clock_out ? timeOfISO(roundToHalfHourISO(modalRow.clock_out)) : "");
  };
  const presetNotRecord = () => {
    setComputedInDraft("");
    setComputedOutDraft("");
  };

  const computedInISO = modalRow ? combineDraftToISO(modalRow.date, computedInDraft) : null;
  const computedOutISO = modalRow ? combineDraftToISO(modalRow.date, computedOutDraft, computedInDraft) : null;
  // Giờ công = (Ra − Vào được tính) + Σ "Điều chỉnh"; Giờ OT = Σ khung "Ngoài
  // giờ" — cùng công thức server áp khi lưu (registry khối "Kết quả (tạm tính)").
  const adjustmentHours = round2(
    exceptionsDraft.filter((e) => e.kind === "adjustment").reduce((sum, e) => sum + (e.hours ?? 0), 0),
  );
  const previewOtHours = round2(
    exceptionsDraft
      .filter((e) => e.kind === "overtime")
      .reduce((sum, e) => sum + exceptionSpanHours(e.from_time, e.to_time), 0),
  );
  const previewBaseHours =
    computedInISO && computedOutISO ? Math.max(0, round2((new Date(computedOutISO).getTime() - new Date(computedInISO).getTime()) / 3_600_000)) : 0;
  const previewWorkHours = round2(previewBaseHours + adjustmentHours);
  // Nhãn "lệch giờ" của bản ghi — mỗi nhãn cần Quản lý CHỌN RÕ "Điều chỉnh"
  // (thêm dòng trừ/cộng giờ ở khối [18e]) hay "Bỏ qua" (xác nhận không cần
  // điều chỉnh gì) trước khi ghi nhận công thật. Chỉ tính nhãn nói về giờ
  // giấc; "Có khiếu nại"/"Thiếu chấm công" là loại khác, không phải thứ đem
  // trừ giờ.
  const deviationLabels = (modalRow?.status_labels ?? [])
    .map((code, i) => (DEVIATION_STATUS_CODES.has(code) ? { code, labelVi: modalRow?.status_labels_vi[i] ?? code } : null))
    .filter((x): x is { code: string; labelVi: string } => Boolean(x));
  const undecidedLabels = deviationLabels.filter(({ code }) => !labelDecisions[code]);
  // "Điều chỉnh" luôn thêm 1 dòng "Điều chỉnh" mới ghi rõ nhãn nguồn gốc
  // trong Ghi chú — Quản lý vẫn tự gõ số giờ/sửa ghi chú như dòng thường.
  const decideLabel = (code: string, labelVi: string, choice: "adjust" | "ignore") => {
    setLabelDecisions((prev) => ({ ...prev, [code]: choice }));
    if (choice === "adjust") {
      setExceptionsDraft((prev) => [...prev, { kind: "adjustment", hours: null, note: labelVi }]);
    }
  };
  const exceptionErrors = exceptionsDraft.map(validateExceptionLine);
  const exceptionsInvalid = exceptionErrors.some(Boolean) || previewWorkHours < 0;
  // Đang "Sửa lại" 1 record ĐÃ CHỐT mà KHÔNG đổi field nào (so với chính giá
  // trị đã lưu của `modalRow`, không phải so với lần render trước) → "Ghi
  // nhận công" không có gì mới để lưu, khoá lại (đúng kiểu khoá "còn dòng
  // ngoại lệ chưa hợp lệ" — kèm title giải thích) thay vì bấm được rồi gửi y
  // nguyên dữ liệu cũ lên server (BUG THẬT: 2026-09-08, user báo qua ảnh chụp
  // UI — bấm "Sửa lại" không đổi gì, nút vẫn sáng bình thường).
  const isRecordUnchangedFromSaved =
    !!modalRow &&
    computedInDraft === timeOfISO(modalRow.computed_check_in) &&
    computedOutDraft === timeOfISO(modalRow.computed_check_out) &&
    managerNoteDraft === (modalRow.manager_notes ?? "") &&
    matchedCode === (modalRow.is_matched ? modalRow.shift_code ?? "" : "") &&
    exceptionsDraft.length === modalRow.exceptions.length &&
    exceptionsDraft.every((line, i) => {
      const saved = modalRow.exceptions[i];
      return (
        line.kind === saved.kind &&
        line.from_time === saved.from_time &&
        line.to_time === saved.to_time &&
        line.hours === saved.hours &&
        (line.note ?? "") === (saved.note ?? "")
      );
    });
  const isEditingAlreadyResolvedRow = !!modalRow && editMode && modalRow.action_state !== "pending_review";
  // Popover chặn "Ghi nhận công" tự đóng + tự mở dialog [19] ngay khi nhãn
  // CUỐI CÙNG được quyết định — Quản lý không phải bấm thêm 1 nút "Tiếp tục".
  // "Điều chỉnh" thêm dòng RỖNG (chưa có số giờ) — nếu đó là quyết định CUỐI
  // CÙNG, dòng đó khiến `exceptionsInvalid=true` ngay lúc đóng popover; không
  // mở dialog [19] trong trường hợp này (sẽ luôn bị chặn ở đó), trả Quản lý về
  // thẳng khối `[18e]` để điền nốt dòng vừa thêm.
  useEffect(() => {
    if (pendingDecisionOpen && undecidedLabels.length === 0) {
      setPendingDecisionOpen(false);
      if (!exceptionsInvalid) openConfirm("record");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDecisionOpen, undecidedLabels.length, exceptionsInvalid]);

  // Cảnh báo chồng giờ — so "Được tính" đang chỉnh với bản ghi KHÁC cùng nhân
  // viên + cùng ngày đã có cặp Được tính (registry STATE `OVERLAP-WARN`).
  const overlapRow = useMemo(() => {
    if (!modalRow || !computedInISO || !computedOutISO) return null;
    const a1 = new Date(computedInISO).getTime();
    const a2 = new Date(computedOutISO).getTime();
    return (
      flatRows.find((r) => {
        if (r.attendance_ref === modalRow.attendance_ref) return false;
        if (r.user_id !== modalRow.user_id || r.date !== modalRow.date) return false;
        if (!r.computed_check_in || !r.computed_check_out) return false;
        const b1 = new Date(r.computed_check_in).getTime();
        const b2 = new Date(r.computed_check_out).getTime();
        return a1 < b2 && b1 < a2;
      }) ?? null
    );
  }, [modalRow, computedInISO, computedOutISO, flatRows]);

  const openConfirm = (action: "record" | "defer" | "reject_close") => {
    setConfirmAction(action);
  };
  // "Ghi nhận công" — nếu còn nhãn lệch giờ CHƯA quyết định "Điều chỉnh"/"Bỏ
  // qua", chặn lại bằng popover riêng (KHÔNG mở thẳng dialog [19]) để buộc
  // Quản lý chọn dứt điểm trước khi ghi nhận công thật. Đủ nhãn đã quyết định
  // (kể cả khi record vốn không có nhãn lệch giờ nào) → mở dialog [19] như cũ.
  const handleRecordClick = () => {
    if (undecidedLabels.length > 0) {
      setPendingDecisionOpen(true);
      return;
    }
    openConfirm("record");
  };

  const submitResolve = () => {
    if (!modalRow || !confirmAction) return;
    const payload: ResolveRequest =
      confirmAction === "defer"
        ? { action: "defer" }
        : confirmAction === "reject_close"
          ? { action: "reject_close", manager_note: managerNoteDraft.trim() || null }
          : {
            action: "record",
            computed_check_in: computedInISO,
            computed_check_out: computedOutISO,
            manager_note: managerNoteDraft.trim() || null,
            exceptions: exceptionsDraft,
          };
    resolveMutation.mutate(
      // Dòng "Thiếu chấm công" chưa có bản ghi chấm công → định danh bằng ca.
      { attendanceId: modalRow.attendance_id, scheduleId: modalRow.schedule_id, payload },
      {
        onSuccess: (res) => {
          setToast(res.message);
          setConfirmAction(null);
          closeModal();
        },
        onError: (error) => {
          // 2026-09-07 (sync wireframe STATE ACTION-ERROR, dòng 5451-5453):
          // báo lỗi bằng TOAST (thông báo rời, không chặn modal) — trước đây
          // hiện banner cố định TRONG thân modal, khác cơ chế wireframe mô
          // tả. Vẫn giữ nguyên modal + dữ liệu đang nhập (không đóng, không
          // reset draft) — đúng yêu cầu cốt lõi, chỉ đổi CÁCH báo.
          setToast((error as { message?: string })?.message || "Thao tác không thành công. Thử lại");
          setConfirmAction(null);
        },
      },
    );
  };

  // ── Checkbox cột "Thao tác" + thanh bulk ──

  const openBulkConfirm = (kind: "record" | "defer") => {
    if (selectedRows.length === 0) return;
    setBulkConfirmAction(kind);
  };

  const submitBulkResolve = () => {
    if (!bulkConfirmAction || selectedRows.length === 0) return;
    // "Bulk KHÔNG có field điền-giùm nào" — gửi lại NGUYÊN giá trị hệ thống đã
    // tự tính đang hiển thị sẵn cho từng dòng, không phải giá trị Quản lý gõ
    // tay (khác `submitResolve` ở trên, vốn gửi *Draft từ state của modal đơn).
    const items: BulkResolveItem[] = selectedRows.map((r) => ({
      ref: r.attendance_ref as string,
      computed_check_in: r.computed_check_in,
      computed_check_out: r.computed_check_out,
      exceptions: r.exceptions,
    }));
    const verbVi = bulkConfirmAction === "record" ? "Đã ghi nhận công" : "Đã hoãn ghi nhận";
    bulkResolveMutation.mutate(
      bulkConfirmAction === "record"
        ? { action: "record", items, manager_note: bulkReason.trim() }
        : { action: "defer", items },
      {
        onSuccess: (res) => {
          setToast(res.failed > 0 ? `${verbVi} ${res.processed} bản ghi · ${res.failed} lỗi` : `${verbVi} ${res.processed} bản ghi`);
          setSelectedIds(new Set());
          setBulkConfirmAction(null);
          setBulkReason("");
        },
        onError: (error) => {
          setToast((error as { message?: string })?.message || "Thao tác không thành công. Thử lại");
          setBulkConfirmAction(null);
        },
      },
    );
  };

  // ── [9b] Xuất ▼ ──

  const handleExport = (kind: "all" | "recorded") => {
    setExportError(null);
    const rows = kind === "recorded" ? visibleRows.filter((r) => r.action_state !== "pending_review" && r.action_state !== "upcoming") : visibleRows;
    if (kind === "all") {
      const pendingHere = visibleRows.filter(isPendingRow).length;
      if (pendingHere > 0) {
        setExportError(`Còn ${pendingHere} công cần xử lý trong khoảng ngày này. Xử lý xong trước khi xuất.`);
        return;
      }
    }
    if (rows.length === 0) {
      setToast("Không có dữ liệu để xuất.");
      return;
    }
    setExporting(true);
    try {
      const sheetData = rows.map((r) => ({
        "Nhân viên": r.full_name,
        "Ca": r.shift_code ? `${r.shift_code} • ${r.shift_label}` : r.shift_label,
        "Thực tế": formatTimeRange(r.clock_in, r.clock_out),
        "Được tính": r.computed_check_in || r.computed_check_out ? formatTimeRange(r.computed_check_in, r.computed_check_out) : "—",
        "Địa điểm": r.location_name ?? "—",
        "Trạng thái": r.status_labels_vi.length ? r.status_labels_vi.join(" · ") : "—",
        "Giờ công": r.work_hours != null ? `${formatHoursVi(r.work_hours)}h` : "—",
        "Giờ OT": r.ot_hours != null ? `${formatHoursVi(r.ot_hours)}h` : "—",
        "Ngày": formatDateVi(r.date),
      }));
      const ws = XLSX.utils.json_to_sheet(sheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Cham cong");
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `cham-cong-${stamp}.xlsx`);
      setToast(`Đã xuất ${rows.length} dòng`);
      setExportMenuOpen(false);
    } catch {
      setToast("Xuất không thành công. Thử lại");
    } finally {
      setExporting(false);
    }
  };

  // ── Render: bảng desktop/tablet ──

  // 2026-09-16 (yêu cầu trực tiếp) — kéo-giãn độ rộng cột: bấm-giữ tay kéo ở
  // viền phải mỗi `<th>` (trừ "Thao tác", cột cuối không có tay kéo) rồi rê
  // chuột ngang. `resizingRef` (không phải state) giữ cột đang kéo + điểm
  // bắt đầu — tránh re-render lại TOÀN BỘ handler mỗi lần chuột nhích 1px
  // (chỉ `colWidths` mới cần re-render để width mới áp ngay vào `<colgroup>`).
  const [colWidths, setColWidths] = useState<Record<AttColKey, number>>(DEFAULT_COL_WIDTHS);
  // 2026-09-19 (fix bug thật: kéo giãn/hẹp 1 cột LÀM CÁC CỘT KHÁC đổi độ rộng
  // theo, kể cả cột không hề đụng tới — vd kéo "Ca" khiến "Thực tế" bị che
  // nhãn dù không kéo trực tiếp): `.attTable` trước có `width:100%` — khi
  // TỔNG 9 `colWidths` (khai báo qua `<colgroup>`) KHÁC bề rộng thật của
  // `.attWrap` (gần như luôn khác — 9 số cộng lại chỉ ~1140px trong khi màn
  // hình thường rộng hơn), `table-layout:fixed` CO/GIÃN LẠI TỈ LỆ mọi cột để
  // khớp đúng 100% — render THẬT trên màn hình lệch khỏi giá trị `colWidths`
  // đang giữ trong state, nên rào sàn min-width (đo & ép ở `handleResizeMove`)
  // chỉ đúng với SỐ TRONG STATE, không đúng với PIXEL THẬT sau khi trình
  // duyệt co giãn lại. Set `width` = ĐÚNG TỔNG 9 cột (không phải 100%) buộc
  // trình duyệt render CHÍNH XÁC từng cột theo state, không co giãn tỉ lệ —
  // dư/thiếu so với `.attWrap` xử lý bằng `overflow-x:auto` sẵn có (an toàn,
  // đã ghi ở comment `.attWrap` cũ), không phải giãn cột để lấp đầy.
  const tableWidth = useMemo(() => Object.values(colWidths).reduce((sum, w) => sum + w, 0), [colWidths]);
  // 2026-09-19 — sàn resize RIÊNG theo từng cột, đo THẬT qua DOM (không đoán
  // pixel/ký tự): mỗi `<span>` bọc nhãn (chữ + icon tooltip, KHÔNG gồm tay
  // kéo) gắn 1 ref vào đây; đo 1 LẦN lúc mount (nhãn tĩnh, không đổi theo
  // ngôn ngữ/dữ liệu trong phiên) bằng `getBoundingClientRect().width` — đo
  // được kể cả khi `<th>` đang hẹp hơn nhãn thật, vì `<th>` KHÔNG
  // `overflow:hidden` (xem CSS) nên nhãn vẫn giữ nguyên kích thước tự nhiên,
  // chỉ tràn hình chứ không co lại.
  const colLabelRefs = useRef<Partial<Record<AttColKey, HTMLElement | null>>>({});
  const setColLabelRef = (key: AttColKey) => (el: HTMLElement | null) => {
    colLabelRefs.current[key] = el;
  };
  // `handleResizeMove` bên dưới là `useCallback([])` (giữ NGUYÊN 1 reference
  // suốt đời component — bắt buộc để add/removeEventListener khớp đúng cặp,
  // xem comment ở khai báo) nên đọc số đo qua REF (không phải state — không
  // có UI nào cần re-render theo số đo này, chỉ `handleResizeMove` đọc lúc
  // kéo chuột).
  const colMinWidthsRef = useRef<Partial<Record<AttColKey, number>>>({});
  useLayoutEffect(() => {
    const measured: Partial<Record<AttColKey, number>> = {};
    (Object.keys(colLabelRefs.current) as AttColKey[]).forEach((key) => {
      const el = colLabelRefs.current[key];
      if (el) measured[key] = Math.ceil(el.getBoundingClientRect().width) + HEADER_CELL_PADDING_X;
    });
    colMinWidthsRef.current = measured;
  }, []);
  const resizingRef = useRef<{ key: AttColKey; startX: number; startWidth: number } | null>(null);
  const handleResizeMove = useCallback((e: MouseEvent) => {
    const r = resizingRef.current;
    if (!r) return;
    const floor = Math.max(MIN_COL_WIDTH, colMinWidthsRef.current[r.key] ?? 0);
    const next = Math.max(floor, r.startWidth + (e.clientX - r.startX));
    setColWidths((prev) => (prev[r.key] === next ? prev : { ...prev, [r.key]: next }));
  }, []);
  const handleResizeUp = useCallback(() => {
    resizingRef.current = null;
    document.removeEventListener("mousemove", handleResizeMove);
    document.removeEventListener("mouseup", handleResizeUp);
  }, [handleResizeMove]);
  const handleResizeDown = (key: AttColKey) => (e: React.MouseEvent) => {
    e.preventDefault();
    resizingRef.current = { key, startX: e.clientX, startWidth: colWidths[key] };
    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeUp);
  };
  // Dọn listener nếu component unmount NGAY GIỮA lúc đang kéo (đổi trang/tab
  // khi chưa buông chuột) — tránh rò rỉ listener trỏ vào state đã mất.
  useEffect(
    () => () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeUp);
    },
    [handleResizeMove, handleResizeUp],
  );
  const renderColResizeHandle = (key: AttColKey) => (
    <span
      className={styles.colResizeHandle}
      onMouseDown={handleResizeDown(key)}
      title="Kéo để đổi độ rộng cột"
      aria-hidden="true"
    />
  );

  const renderHeader = () => (
    <tr>
      <th className={styles.colEmp} scope="col">
        <span ref={setColLabelRef("emp")}>Nhân viên</span>
        {renderColResizeHandle("emp")}
      </th>
      <th scope="col">
        <span ref={setColLabelRef("ca")}>
          Ca <PortalTooltip text={COL_TOOLTIPS.ca} className={styles.infoIco} tabIndex={0} ariaLabel={COL_TOOLTIPS.ca}><InfoIcon /></PortalTooltip>
        </span>
        {renderColResizeHandle("ca")}
      </th>
      <th scope="col">
        <span ref={setColLabelRef("thucTe")}>
          Thực tế <PortalTooltip text={COL_TOOLTIPS.thucTe} className={styles.infoIco} tabIndex={0} ariaLabel={COL_TOOLTIPS.thucTe}><InfoIcon /></PortalTooltip>
        </span>
        {renderColResizeHandle("thucTe")}
      </th>
      <th scope="col">
        <span ref={setColLabelRef("duocTinh")}>
          Được tính <PortalTooltip text={COL_TOOLTIPS.duocTinh} className={styles.infoIco} tabIndex={0} ariaLabel={COL_TOOLTIPS.duocTinh}><InfoIcon /></PortalTooltip>
        </span>
        {renderColResizeHandle("duocTinh")}
      </th>
      <th scope="col">
        <span ref={setColLabelRef("diaDiem")}>
          Địa điểm <PortalTooltip text={COL_TOOLTIPS.diaDiem} className={styles.infoIco} tabIndex={0} ariaLabel={COL_TOOLTIPS.diaDiem}><InfoIcon /></PortalTooltip>
        </span>
        {renderColResizeHandle("diaDiem")}
      </th>
      <th scope="col">
        <span ref={setColLabelRef("trangThai")}>
          Trạng thái <PortalTooltip text={COL_TOOLTIPS.trangThai} className={styles.infoIco} tabIndex={0} ariaLabel={COL_TOOLTIPS.trangThai}><InfoIcon /></PortalTooltip>
        </span>
        {renderColResizeHandle("trangThai")}
      </th>
      <th className={styles.num} scope="col">
        <span ref={setColLabelRef("gioCong")}>
          Giờ công <PortalTooltip text={COL_TOOLTIPS.gioCong} className={styles.infoIco} tabIndex={0} ariaLabel={COL_TOOLTIPS.gioCong}><InfoIcon /></PortalTooltip>
        </span>
        {renderColResizeHandle("gioCong")}
      </th>
      <th className={styles.num} scope="col">
        <span ref={setColLabelRef("gioOt")}>
          Giờ OT <PortalTooltip text={COL_TOOLTIPS.gioOt} className={styles.infoIco} tabIndex={0} ariaLabel={COL_TOOLTIPS.gioOt}><InfoIcon /></PortalTooltip>
        </span>
        {renderColResizeHandle("gioOt")}
      </th>
      <th className={styles.colAct} scope="col">
        {/* 2026-09-13 (yêu cầu trực tiếp, sửa lại lần 2): checkbox "chọn tất
            cả" nằm CHUNG cột "Thao tác", CÙNG HÀNG với nút hành động, đứng
            TRƯỚC (bên trái) — bản trước xếp checkbox thành khối riêng phía
            TRÊN nút (dọc) là sai, ảnh chụp màn hình cho thấy rõ. Dùng flex
            row thay vì display:block để 2 phần tử nằm ngang cùng 1 dòng. */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "var(--nqh-space-1)" }}>
          <input
            type="checkbox"
            checked={allSelected}
            disabled={selectableRows.length === 0}
            ref={(el) => {
              if (el) el.indeterminate = someSelected;
            }}
            onChange={toggleSelectAll}
            aria-label="Chọn tất cả dòng cần xử lý"
          />
          <span>Thao tác</span>
        </div>
      </th>
    </tr>
  );

  const renderSelectCellInner = (row: TeamAttendanceRow) => {
    if (!isPendingRow(row) || !row.attendance_ref || isOwnerReadOnly) return null;
    const ref = row.attendance_ref;
    return (
      <input
        type="checkbox"
        checked={selectedIds.has(ref)}
        onChange={() => toggleRowSelect(ref)}
        aria-label={`Chọn ${row.full_name}`}
      />
    );
  };

  const renderActionCell = (row: TeamAttendanceRow) => (
    // Bug thật 2026-09-07 (sync wireframe MA TRẬN PHÂN QUYỀN, phát hiện qua
    // test OWNER read-only): bản trước tự dựng lại y hệt nội dung của
    // `renderActionCellInner` (3 lần: đây, khối merge bên dưới, 2 khối card
    // mobile) — khi thêm khoá OWNER vào `renderActionCellInner` thì nhánh
    // KHÔNG-gộp (hầu hết trường hợp) không hề được khoá vì nó không gọi qua
    // hàm đó. Delegate về `renderActionCellInner` để CHỈ MỘT nơi quyết định
    // nhãn/khoá/onClick — <td> ở đây chỉ còn lo bọc ô.
    // Checkbox + nút CÙNG HÀNG (flex row), checkbox đứng TRƯỚC bên trái.
    <td className={styles.colAct}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "var(--nqh-space-1)" }}>
        {renderSelectCellInner(row)}
        {renderActionCellInner(row)}
      </div>
    </td>
  );

  const renderDataRow = (row: TeamAttendanceRow, empCell: React.ReactNode, actCell: React.ReactNode) => (
    <tr key={row.attendance_ref ?? `${row.schedule_id}-${row.user_id}`}>
      {empCell}
      <td>{row.shift_code ? `${row.shift_code} • ${row.shift_label}` : row.shift_label}</td>
      <td>{formatTimeRange(row.clock_in, row.clock_out)}</td>
      <td>{row.computed_check_in || row.computed_check_out ? formatTimeRange(row.computed_check_in, row.computed_check_out) : "—"}</td>
      <td>{row.location_name ?? "—"}</td>
      <td><StatusLabels codes={row.status_labels} labelsVi={row.status_labels_vi} /></td>
      <td className={styles.num}>{row.work_hours != null ? `${formatHoursVi(row.work_hours)}h` : "—"}</td>
      <td className={styles.num}>{row.ot_hours != null ? `${formatHoursVi(row.ot_hours)}h` : "—"}</td>
      {actCell}
    </tr>
  );

  const renderGroupRows = (group: RowGroup) => {
    const canMerge = canMergeActionCell(group.rows);
    if (group.rows.length > 1 && canMerge) {
      return group.rows.map((row, i) =>
        renderDataRow(
          row,
          i === 0 ? (
            <td className={styles.colEmp} rowSpan={group.rows.length}>
              {row.full_name}
            </td>
          ) : null,
          // `canMergeActionCell` chỉ true khi `!isPendingRow(rows[0])` (xem
          // định nghĩa) — nhóm được gộp Thao tác KHÔNG BAO GIỜ là hàng-chờ-
          // xử-lý, nên `renderSelectCellInner(row)` ở đây luôn trả null
          // (không tự vẽ checkbox cho record đã chốt). Gọi chung 1 chỗ với
          // nhánh không-gộp bên dưới để KHÔNG có 2 nơi quyết định vị trí
          // checkbox trong ô "Thao tác".
          i === 0 ? (
            <td className={styles.colAct} rowSpan={group.rows.length}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "var(--nqh-space-1)" }}>
                {renderSelectCellInner(row)}
                {renderActionCellInner(row)}
              </div>
            </td>
          ) : null,
        ),
      );
    }
    return group.rows.map((row) =>
      renderDataRow(
        row,
        <td className={styles.colEmp}>{row.full_name}</td>,
        renderActionCell(row),
      ),
    );
  };

  const renderActionCellInner = (row: TeamAttendanceRow) => {
    if (row.action_state === "upcoming") return "—";
    const label = row.action_state === "pending_review" ? "Xác nhận công" : row.action_state === "recorded" ? "✓ Đã ghi nhận" : "✕ Không ghi nhận";
    const cls = row.action_state === "pending_review" ? styles.actPending : styles.actDone;
    // OWNER chỉ đọc — bản ghi Xác nhận công (hành động GHI) bị khoá tại chỗ;
    // 2 nhãn còn lại (✓/✕) vẫn bấm được để XEM chi tiết (modal tự vào RO).
    if (isOwnerReadOnly && row.action_state === "pending_review") {
      return (
        <button type="button" className={`${styles.actBtn} ${cls}`} disabled title="Chủ doanh nghiệp đang ở chế độ chỉ đọc">
          {label}
        </button>
      );
    }
    return (
      <button type="button" className={`${styles.actBtn} ${cls}`} onClick={() => openModal(row)}>
        {label}
      </button>
    );
  };

  const renderTable = () => (
    <div className={styles.attWrap}>
      <table className={styles.attTable} style={{ width: tableWidth }}>
        {/* `table-layout: fixed` (CSS) đọc độ rộng từ ĐÂY, KHÔNG phải từ
            `width` trên từng `<th>`/`<td>` — 1 nguồn duy nhất cho mỗi cột,
            khớp thứ tự 9 cột thật đang render (xem `renderHeader`). */}
        <colgroup>
          <col style={{ width: colWidths.emp }} />
          <col style={{ width: colWidths.ca }} />
          <col style={{ width: colWidths.thucTe }} />
          <col style={{ width: colWidths.duocTinh }} />
          <col style={{ width: colWidths.diaDiem }} />
          <col style={{ width: colWidths.trangThai }} />
          <col style={{ width: colWidths.gioCong }} />
          <col style={{ width: colWidths.gioOt }} />
          <col style={{ width: colWidths.act }} />
        </colgroup>
        <thead>{renderHeader()}</thead>
        <tbody>
          {sections.every((s) => s.groups.length === 0) ? (
            <tr>
              <td colSpan={9} className={styles.emptyStateCell}>
                {pendingFilterOn ? "Không còn bản ghi nào cần xử lý trong phạm vi hiện tại. 🎉" : "Không có bản ghi chấm công cho ngày này."}
              </td>
            </tr>
          ) : (
            sections.map((section, si) => (
              <React.Fragment key={section.dateLabel ?? `s${si}`}>
                {isRangeMode && section.dateLabel && (
                  <tr className={styles.dateHeadingRow}>
                    <th colSpan={9} scope="colgroup">{formatDateVi(section.dateLabel)}</th>
                  </tr>
                )}
                {section.groups.map((g) => (
                  <React.Fragment key={g.key}>{renderGroupRows(g)}</React.Fragment>
                ))}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  // ── Render: mobile cards ──

  const renderCards = () => (
    <div className={styles.attCards}>
      {sections.every((s) => s.groups.length === 0) ? (
        <div className={styles.emptyState}>
          {pendingFilterOn ? "Không còn bản ghi nào cần xử lý trong phạm vi hiện tại. 🎉" : "Không có bản ghi chấm công cho ngày này."}
        </div>
      ) : (
        sections.map((section, si) => (
          <React.Fragment key={section.dateLabel ?? `s${si}`}>
            {isRangeMode && section.dateLabel && <div className={styles.dateCardHead}>{formatDateVi(section.dateLabel)}</div>}
            {section.groups.map((g) => {
              const canMergeAction = canMergeActionCell(g.rows);
              return (
                <div className={styles.attCard} key={g.key}>
                  <div className={styles.attCardName}>{g.rows[0].full_name}</div>
                  {g.rows.map((row, i) => {
                    const selectableRef = isPendingRow(row) && !isOwnerReadOnly ? row.attendance_ref : null;
                    return (
                    <React.Fragment key={row.attendance_ref ?? i}>
                      {i > 0 && <div className={styles.cardSep} />}
                      <div className={styles.attShift}>
                        <div>Ca: {row.shift_code ? `${row.shift_code} • ${row.shift_label}` : row.shift_label}</div>
                        <div>Thực tế: {formatTimeRange(row.clock_in, row.clock_out)}</div>
                        <div>Được tính: {row.computed_check_in || row.computed_check_out ? formatTimeRange(row.computed_check_in, row.computed_check_out) : "—"}</div>
                        <div>
                          {/* ☐ CHỈ hiện trên dòng hàng-chờ-xử-lý, ẨN HẲN (không mờ) ở dòng
                              đã ghi nhận/không ghi nhận — tránh chạm nhầm (R10, sync prototype
                              `att-card__kv` khối "Địa điểm"). */}
                          {selectableRef && (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(selectableRef)}
                              onChange={() => toggleRowSelect(selectableRef)}
                              aria-label={`Chọn ${row.full_name}`}
                              style={{ marginRight: "var(--nqh-space-2)" }}
                            />
                          )}
                          Địa điểm: {row.location_name ?? "—"}
                        </div>
                        <div>Trạng thái: <StatusLabels codes={row.status_labels} labelsVi={row.status_labels_vi} /></div>
                        <div>
                          Giờ công: {row.work_hours != null ? `${formatHoursVi(row.work_hours)}h` : "—"}
                          {row.ot_hours != null ? ` · OT ${formatHoursVi(row.ot_hours)}h` : ""}
                        </div>
                        {/* Bug 1 fix (QA 2026-08-17): dropped `|| i === g.rows.length - 1` —
                            it fired for the LAST row regardless of `canMergeAction`, duplicating
                            the shared-button block below (2 buttons opening 2 different
                            records). The shared block already covers the canMergeAction===true
                            case for the whole group. */}
                        {!canMergeAction && row.action_state !== "upcoming" && (
                          <button
                            type="button"
                            className={`${styles.actBtn} ${styles.actBtnBlock} ${row.action_state === "pending_review" ? styles.actPending : styles.actDone}`}
                            disabled={isOwnerReadOnly && row.action_state === "pending_review"}
                            title={isOwnerReadOnly && row.action_state === "pending_review" ? "Chủ doanh nghiệp đang ở chế độ chỉ đọc" : undefined}
                            onClick={() => openModal(row)}
                          >
                            {row.action_state === "pending_review" ? "Xác nhận công" : row.action_state === "recorded" ? "✓ Đã ghi nhận" : "✕ Không ghi nhận"}
                          </button>
                        )}
                      </div>
                    </React.Fragment>
                    );
                  })}
                  {canMergeAction && g.rows.length > 1 && g.rows[0].action_state !== "upcoming" && (
                    <button
                      type="button"
                      className={`${styles.actBtn} ${styles.actBtnBlock} ${g.rows[0].action_state === "pending_review" ? styles.actPending : styles.actDone}`}
                      disabled={isOwnerReadOnly && g.rows[0].action_state === "pending_review"}
                      title={isOwnerReadOnly && g.rows[0].action_state === "pending_review" ? "Chủ doanh nghiệp đang ở chế độ chỉ đọc" : undefined}
                      onClick={() => openModal(g.rows[0])}
                    >
                      {g.rows[0].action_state === "pending_review" ? "Xác nhận công" : g.rows[0].action_state === "recorded" ? "✓ Đã ghi nhận" : "✕ Không ghi nhận"}
                    </button>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))
      )}
    </div>
  );

  // ── Render: modal [18]/[18-RO] ──

  /**
   * Khối `[18e]` "Xử lý ngoại lệ giờ công" — 1 danh sách hợp nhất, 2 LOẠI dòng
   * dùng chung 1 nút thêm. Tiêu đề LUÔN hiện; BẢNG ẩn hẳn khi 0 dòng (quy tắc
   * ẩn-khi-rỗng của registry). Chế độ Xem: bảng read-only, không ✕/+ Thêm.
   */
  const renderExceptionBlock = (editable: boolean) => {
    if (!editable && exceptionsDraft.length === 0) return null;
    const update = (idx: number, patch: Partial<AttendanceExceptionLine>) =>
      setExceptionsDraft((prev) => prev.map((line, i) => (i === idx ? { ...line, ...patch } : line)));
    return (
      <div className={styles.excBlock}>
        <div className={styles.excHead}>
          <strong>
            Xử lý ngoại lệ giờ công{exceptionsDraft.length ? ` (${exceptionsDraft.length})` : ""}
          </strong>
          <PortalTooltip text={EXCEPTION_BLOCK_TOOLTIP} className={styles.infoIco} tabIndex={0} ariaLabel={EXCEPTION_BLOCK_TOOLTIP}>
            <InfoIcon />
          </PortalTooltip>
        </div>
        {/* 1 dòng quyết định / nhãn lệch giờ — CHỌN "Điều chỉnh" (thêm dòng
            trừ/cộng giờ bên dưới) hay "Bỏ qua" (xác nhận không cần điều
            chỉnh). Bắt buộc chọn hết trước khi ghi nhận công thật — xem
            `undecidedLabels`/popover chặn ở nút "Ghi nhận công". */}
        {editable && deviationLabels.length > 0 && (
          <div className={styles.labelDecisions}>
            {deviationLabels.map(({ code, labelVi }) => (
              <div key={code} className={styles.labelDecisionRow}>
                <span>
                  Nhãn: <strong>{labelVi}</strong>
                </span>
                <div className={styles.labelDecisionActions}>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnSm} ${labelDecisions[code] === "adjust" ? styles.btnPrimary : styles.btnSecondary}`}
                    onClick={() => decideLabel(code, labelVi, "adjust")}
                  >
                    Điều chỉnh
                  </button>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnSm} ${labelDecisions[code] === "ignore" ? styles.btnPrimary : styles.btnSecondary}`}
                    onClick={() => decideLabel(code, labelVi, "ignore")}
                  >
                    Bỏ qua
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {exceptionsDraft.length > 0 && (
          <table className={styles.excTable}>
            <thead>
              <tr>
                <th>Loại</th>
                <th>Giá trị</th>
                <th>Ghi chú / Lý do</th>
                {editable && <th aria-label="Xoá" />}
              </tr>
            </thead>
            <tbody>
              {exceptionsDraft.map((line, idx) => {
                const err = exceptionErrors[idx];
                return (
                  <React.Fragment key={line.id ?? `new-${idx}`}>
                    <tr>
                      <td>{line.kind === "overtime" ? "Ngoài giờ" : "Điều chỉnh"}</td>
                      <td>
                        {line.kind === "overtime" ? (
                          editable ? (
                            <span className={styles.excVal}>
                              <input
                                type="time"
                                className={styles.timeInput}
                                aria-label="Ngoài giờ từ"
                                value={line.from_time ?? ""}
                                onChange={(e) => update(idx, { from_time: e.target.value })}
                              />
                              –
                              <input
                                type="time"
                                className={styles.timeInput}
                                aria-label="Ngoài giờ đến"
                                value={line.to_time ?? ""}
                                onChange={(e) => update(idx, { to_time: e.target.value })}
                              />
                            </span>
                          ) : (
                            `${line.from_time ?? "—"}–${line.to_time ?? "—"} (${formatHoursVi(exceptionSpanHours(line.from_time, line.to_time))}h)`
                          )
                        ) : editable ? (
                          <span className={styles.excVal}>
                            <input
                              type="number"
                              step="0.5"
                              className={styles.input}
                              aria-label="Số giờ điều chỉnh"
                              value={line.hours ?? ""}
                              onChange={(e) => update(idx, { hours: e.target.value === "" ? null : Number(e.target.value) })}
                            />
                            h
                          </span>
                        ) : (
                          `${(line.hours ?? 0) > 0 ? "+" : "−"} ${formatHoursVi(Math.abs(line.hours ?? 0))}h`
                        )}
                      </td>
                      <td>
                        {editable ? (
                          <input
                            className={styles.input}
                            value={line.note ?? ""}
                            placeholder={line.kind === "overtime" ? "Ghi chú" : "Lý do (bắt buộc)"}
                            aria-label={line.kind === "overtime" ? "Ghi chú ngoài giờ" : "Lý do điều chỉnh"}
                            onChange={(e) => update(idx, { note: e.target.value })}
                          />
                        ) : (
                          line.note || "—"
                        )}
                      </td>
                      {editable && (
                        <td>
                          <button
                            type="button"
                            className={styles.btnIcon}
                            aria-label="Xoá dòng ngoại lệ"
                            onClick={() => setExceptionsDraft((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            <CloseIcon />
                          </button>
                        </td>
                      )}
                    </tr>
                    {err && (
                      <tr>
                        <td colSpan={editable ? 4 : 3}>
                          <span className={styles.fieldError}>{err}</span>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        {editable && (
          <div className={styles.excAddRow}>
            <span className={styles.menuAnchor} onMouseDown={(e) => e.stopPropagation()}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                aria-haspopup="true"
                aria-expanded={excMenuOpen}
                onClick={() => setExcMenuOpen((v) => !v)}
              >
                + Thêm <ChevronDownIcon />
              </button>
              {excMenuOpen && (
                <div className={`${styles.menu} ${styles.menuRight} ${styles.excMenu}`} role="menu">
                  <button
                    type="button"
                    className={styles.excMenuRow}
                    role="menuitem"
                    onClick={() => {
                      setExceptionsDraft((prev) => [...prev, { kind: "overtime", from_time: "", to_time: "", note: "" }]);
                      setExcMenuOpen(false);
                    }}
                  >
                    <b>+ Đăng ký ngoài giờ</b>
                    <span>Khung giờ Từ–Đến làm thêm, có thể trùng hoặc không trùng Vào/Ra được tính</span>
                  </button>
                  <button
                    type="button"
                    className={styles.excMenuRow}
                    role="menuitem"
                    onClick={() => {
                      setExceptionsDraft((prev) => [...prev, { kind: "adjustment", hours: null, note: "" }]);
                      setExcMenuOpen(false);
                    }}
                  >
                    <b>+ Điều chỉnh giờ công (+/-)</b>
                    <span>Cộng/trừ số giờ cố định vào Giờ công, không gắn khung giờ cụ thể</span>
                  </button>
                </div>
              )}
            </span>
          </div>
        )}
        {previewWorkHours < 0 && <div className={styles.fieldError}>Giờ công sau điều chỉnh không được âm</div>}
      </div>
    );
  };

  const renderModal = () => {
    if (!modalRow) return null;
    const row = modalRow;
    const siblingMatched = flatRows.filter(
      (r) => r.user_id === row.user_id && r.date === row.date && r.is_matched && r.attendance_ref !== row.attendance_ref,
    );
    return (
      <div className={styles.modalOverlay}>
        <div className={`${styles.modal} ${styles.modalDetail}`} role="dialog" aria-modal="true" aria-labelledby="sc011ModalTitle">
          <div className={styles.modalHead}>
            {/* 2026-09-07 (sync wireframe registry — 2 amendment NGƯỢC CHIỀU:
                08-26 dời 🕐 sang TRÁI, rồi 08-28 (bản CHỐT SAU CÙNG, đè lên
                08-26) dời lại về PHẢI sau khi bỏ nút ✕. Bản trước chỉ theo
                amendment 08-26, bỏ sót 08-28 — sửa lại đúng bản chốt cuối:
                tiêu đề bên TRÁI, 🕐 bên PHẢI. */}
            <h2 className={styles.modalTitle} id="sc011ModalTitle">
              Chi tiết chấm công — {row.full_name}
            </h2>
            <button
              type="button"
              className={styles.btnIcon}
              aria-label="Lịch sử xử lý"
              title="Lịch sử xử lý"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                // Popup đặt NGOÀI khung modal (ưu tiên bên phải, hết chỗ thì
                // bên trái) — căn phải theo nút 🕐 sẽ đè lên chính nội dung
                // đang đọc. Không còn chỗ 2 bên (màn hẹp) → rơi về căn phải
                // theo nút như menu thường.
                const modalRect = (e.currentTarget.closest('[role="dialog"]') as HTMLElement | null)?.getBoundingClientRect();
                const gap = 8;
                let left: number | undefined;
                if (modalRect) {
                  if (modalRect.right + gap + AUDIT_POPUP_WIDTH <= window.innerWidth) {
                    left = modalRect.right + gap;
                  } else if (modalRect.left - gap - AUDIT_POPUP_WIDTH >= 0) {
                    left = modalRect.left - gap - AUDIT_POPUP_WIDTH;
                  }
                }
                setAuditLogAnchor({ top: rect.top, bottom: rect.bottom, right: window.innerWidth - rect.right, left });
              }}
            >
              <HistoryIcon />
            </button>
            {/* KHÔNG có [✕] ở header cho modal này (wireframe SC-011, nhắc lại 5+ chỗ) —
                "Hoãn ghi nhận" là lối thoát KHÔNG cam kết duy nhất, để mọi lần đóng đều
                đi qua action_state=PENDING_REVIEW có audit trail, không có đường thoát
                im lặng làm mất draft đang gõ. [18-RO] dùng "Đóng" ở footer thay cho ✕. */}
          </div>
          {auditLogAnchor && <AuditLogPopup row={row} anchor={auditLogAnchor} onClose={() => setAuditLogAnchor(null)} />}
          <div className={styles.modalBody}>
            <div className={styles.attLines}>
              {row.is_matched || row.status_labels.includes("missing") ? (
                // 2026-09-07 (sync wireframe): [18-RO] dùng nhãn NGẮN "Ca:"
                // (không hậu tố "(context)") — khác [18] đang xử lý.
                // 2026-09-07 (BUG THẬT — phát hiện khi verify trực tiếp trên
                // UI live sau fix cột "Ca" ở bảng): record "Thiếu chấm công"
                // (MISSING) có `is_matched=false` (KHÔNG có giờ chấm nào để so
                // khớp) NHƯNG vẫn có ca THẬT đã gán (`scheduled_start`/
                // `shift_code` là dữ liệu Schedule thật, khác UNMATCHED nơi 2
                // field này là "—"/context — xem `manager_attendance.py`
                // dòng ~1442-1452). Trước đây nhánh `!is_matched` gộp chung 2
                // case, khiến MISSING hiện nhầm "record này KHÔNG khớp ca nào
                // trong danh sách" dù ca ĐÃ được gán rõ ràng.
                <div>{isProcessMode ? "Ca (context)" : "Ca"}: {row.shift_code ? `${row.shift_code} • ${row.scheduled_start}–${row.scheduled_end}` : `${row.scheduled_start}–${row.scheduled_end}`}</div>
              ) : (
                // Wireframe/prototype: ca dùng-chung-ngày (nếu có) đứng
                // TRƯỚC, rồi mới tới "— bản ghi này không khớp ca nào trong
                // danh sách" (1 câu, nối bằng dấu — chứ không phải 2 câu rời).
                <div>
                  {isProcessMode ? "Ca (context)" : "Ca"}:{" "}
                  {siblingMatched.length > 0 ? (
                    <>
                      {siblingMatched.map((s) => (s.shift_code ? `${s.shift_code} • ${s.shift_label}` : s.shift_label)).join(", ")} (đã dùng cho bản ghi khác cùng ngày){" "}
                    </>
                  ) : (
                    "— "
                  )}
                  bản ghi này không khớp ca nào trong danh sách
                </div>
              )}
            </div>

            {isProcessMode ? (
              <div className={styles.field}>
                <label htmlFor="matchedCode">Khớp ca</label>
                <select id="matchedCode" className={styles.input} value={matchedCode} onChange={(e) => setMatchedCode(e.target.value)}>
                  {/* 2026-09-07 (sync wireframe): "Không khớp ca nào" phải ở CUỐI
                      danh sách, không phải đầu. */}
                  {(shiftCodesQuery.data?.items ?? [])
                    .filter((i) => i.code)
                    .map((i) => (
                      <option key={i.id} value={i.code ?? ""}>
                        {i.code} • {i.start_time.slice(0, 5)}–{i.end_time.slice(0, 5)}
                      </option>
                    ))}
                  <option value="">Không khớp ca nào</option>
                </select>
                {row.is_matched && matchedCode === row.shift_code && <span className={`${styles.muted} ${styles.txtSm}`}>← auto-match</span>}
              </div>
            ) : (
              <div className={styles.attLines}>
                Khớp ca:{" "}
                {row.is_matched
                  ? // Ca không có mã (khối giờ tuỳ chỉnh) → bỏ luôn dấu "•" thay
                    // vì in "• 08:00–17:00" với vế trái trống.
                    row.shift_code
                    ? `${row.shift_code} • ${row.scheduled_start}–${row.scheduled_end}`
                    : `${row.scheduled_start}–${row.scheduled_end}`
                  : "Không khớp ca nào"}
                {/* 2026-09-07 (sync wireframe [18-RO]): tag "← auto-match" phải
                    hiện cả ở chế độ Xem khi record là auto-match thật, không
                    chỉ chế độ Xử lý. */}
                {row.is_matched && row.resolution_source !== "manager" && (
                  <span className={`${styles.muted} ${styles.txtSm}`}> ← auto-match</span>
                )}
              </div>
            )}

            <div className={styles.attDetailGrid}>
              {/* 2026-09-07 (sync wireframe [18]/[18-RO] khối "Ảnh chấm công",
                  dòng 4805-4809/4946-4949): hiện ẢNH SELFIE THẬT + timemark —
                  hook `useAttendancePhoto`/API `getAttendancePhoto` đã có sẵn
                  từ trước nhưng chưa từng được gọi ở trang này, khối này chỉ
                  render icon + chữ. Ưu tiên ảnh Vào (khớp caption giờ Vào
                  trong ASCII mẫu); fallback ảnh Ra nếu record không có ảnh
                  Vào (vd quên chấm vào, chỉ chấm ra). */}
              {/* Wireframe + prototype vẽ 2 khối này dạng "thẻ" CÓ TIÊU ĐỀ
                  ("┌── Ảnh chấm công ──┐" trong ASCII; `<h4>Ảnh chấm công
                  </h4>`/`<h4>Vị trí GPS</h4>` trong prototype). */}
              {(() => {
                const photo = attendancePhotoQuery.data;
                const src = photo?.clock_in_photo ?? photo?.clock_out_photo ?? null;
                const captionTime = photo?.clock_in_photo ? row.clock_in : row.clock_out;
                if (src) {
                  return (
                    <div>
                      <div className={styles.cardTitle}>Ảnh chấm công</div>
                      <div className={styles.attPhoto}>
                        <img
                          src={`data:image/jpeg;base64,${src}`}
                          alt="Ảnh chấm công"
                          style={{ width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: "var(--nqh-radius-md)" }}
                        />
                        <span>{captionTime ? `${formatTime(captionTime)} · ${row.location_name ?? branchLabel}` : null}</span>
                      </div>
                    </div>
                  );
                }
                return (
                  <div>
                    <div className={styles.cardTitle}>Ảnh chấm công</div>
                    <div className={styles.attPhoto}>
                      <PhotoIcon />
                      <span>
                        {attendancePhotoQuery.isFetching
                          ? "Đang tải ảnh…"
                          : row.clock_in
                            ? `${formatTime(row.clock_in)} · ${row.location_name ?? branchLabel}`
                            : "Chưa có ảnh chấm công"}
                      </span>
                    </div>
                  </div>
                );
              })()}
              <div>
                <div className={styles.cardTitle}>Vị trí GPS</div>
                <div className={styles.attGps}>
                  {/* 2026-09-07 (sync wireframe): "Cơ sở" hiện TRƯỚC trạng thái GPS. */}
                  <div>Cơ sở: {row.location_name ?? "—"}</div>
                  {row.gps_ok != null ? (
                    <div>
                      <strong>
                        GPS: {row.gps_ok ? "trong" : "ngoài"} bán kính cho phép{" "}
                        <span className={`${styles.st} ${row.gps_ok ? styles.stOk : styles.stBad}`}>{row.gps_ok ? "✔" : "✖"}</span>
                        {row.gps_distance_m != null && ` (lệch ${Math.round(row.gps_distance_m)}m)`}
                      </strong>
                      {row.gps_lat != null && row.gps_lng != null && (
                        <div className={styles.muted}>{formatGpsCoords(row.gps_lat, row.gps_lng)}</div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.muted}>Chưa có dữ liệu GPS</div>
                  )}
                </div>
              </div>
            </div>

            {isProcessMode ? (
              <>
                <div className={styles.pairRow}>
                  <span>Vào (thực): {formatTime(row.clock_in)}</span>
                  <span>↔</span>
                  <label>
                    Vào được tính:{" "}
                    <input type="time" className={styles.timeInput} value={computedInDraft} onChange={(e) => setComputedInDraft(e.target.value)} />
                  </label>
                </div>
                <div className={styles.pairRow}>
                  <span>Ra (thực): {formatTime(row.clock_out)}</span>
                  <span>↔</span>
                  <label>
                    Ra được tính:{" "}
                    <input type="time" className={styles.timeInput} value={computedOutDraft} onChange={(e) => setComputedOutDraft(e.target.value)} />
                  </label>
                </div>
                {/* "Ghi chú của nhân viên" — LUÔN đọc-only với Quản lý; ẩn hẳn
                    khi nhân viên không để lại ghi chú nào (không hiện "(trống)"). */}
                {row.employee_note && (
                  <div className={styles.field}>
                    <label>Ghi chú của nhân viên</label>
                    <div className={styles.employeeNote}>"{row.employee_note}"</div>
                  </div>
                )}
                {/* BOT-061 (#873): lý do Timemark — cùng quy tắc ẩn-khi-rỗng
                    + style đọc-only như "Ghi chú của nhân viên" ở trên (khác
                    field: đây là lý do KHI CHẤM CÔNG BẰNG TIMEMARK, không
                    phải ghi chú tự do). Nhãn "Lý do chấm công Timemark" lấy
                    VERBATIM từ `TimemarkForm.tsx` (form nhân viên nhập lý do
                    này lúc chấm công) — không đặt tên mới. */}
                {row.timemark_reason && (
                  <div className={styles.field}>
                    <label>Lý do chấm công Timemark</label>
                    <div className={styles.employeeNote}>"{row.timemark_reason}"</div>
                  </div>
                )}
                <div className={styles.field}>
                  <label htmlFor="mgrNote">Ghi chú quản lý</label>
                  <textarea id="mgrNote" className={styles.input} rows={2} value={managerNoteDraft} onChange={(e) => setManagerNoteDraft(e.target.value)} />
                </div>
                <div className={styles.presetRow}>
                  <button type="button" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} onClick={presetByShift}>
                    Ghi nhận theo ca
                  </button>
                  <button type="button" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} disabled={!row.clock_in} onClick={presetByActual}>
                    Ghi nhận theo giờ
                  </button>
                  <button type="button" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} onClick={presetNotRecord}>
                    Không ghi nhận
                  </button>
                </div>
                {renderExceptionBlock(true)}
              </>
            ) : (
              <div className={styles.attLines}>
                <div>Vào (thực): {formatTime(row.clock_in)} ↔ Vào được tính: {formatTime(row.computed_check_in)}</div>
                <div>Ra (thực): {formatTime(row.clock_out)} ↔ Ra được tính: {formatTime(row.computed_check_out)}</div>
                {row.employee_note && <div>Ghi chú của nhân viên: <span className={styles.muted}>"{row.employee_note}"</span></div>}
                {/* BOT-061 (#873) — bản đọc-only [18-RO], cùng field/nhãn với nhánh xử lý ở trên. */}
                {row.timemark_reason && <div>Lý do chấm công Timemark: <span className={styles.muted}>"{row.timemark_reason}"</span></div>}
                <div>Ghi chú quản lý: <span className={styles.muted}>{row.manager_notes || "(trống)"}</span></div>
              </div>
            )}
            {!isProcessMode && renderExceptionBlock(false)}

            <div className={styles.attLines} style={{ marginTop: "var(--nqh-space-3)" }}>
              <div>
                Trạng thái: <StatusLabels codes={row.status_labels} labelsVi={row.status_labels_vi} />
                {/* Hậu tố "Đã ghi nhận (…)" chỉ đúng với bản ghi ĐÃ ghi nhận
                    thật (Thao tác "✓ Đã ghi nhận"). Bản ghi đang chờ xử lý —
                    kể cả loại chỉ-xem như "Thiếu chấm công" — chưa ghi nhận gì. */}
                {row.action_state === "recorded" && (
                  <span className={styles.muted}> · {row.resolution_source === "manager" ? "Đã ghi nhận (Quản lý xử lý)" : "Đã ghi nhận (tự động)"}</span>
                )}
              </div>
              {/* 2026-09-07 (sync wireframe — chốt 2026-08-28): "Kết quả (tạm
                  tính)" phải hiện NGAY DƯỚI "Trạng thái", không phải trước nó
                  — trước đây khối này nằm ở nhánh JSX render TRƯỚC "Trạng
                  thái" trong DOM dù cùng ở cuối modal, đảo ngược thứ tự đọc. */}
              {isProcessMode && (
                // Wireframe + prototype: 1 DÒNG DUY NHẤT — "Kết quả (tạm
                // tính): Giờ công 9,0h · Giờ OT 1,5h".
                <div style={{ marginTop: "var(--nqh-space-2)" }}>
                  <strong>Kết quả (tạm tính):</strong> Giờ công <strong>{formatHoursVi(previewWorkHours)}h</strong> ·{" "}
                  Giờ OT <strong>{formatHoursVi(previewOtHours)}h</strong>
                </div>
              )}
              {!isProcessMode && (
                <div>
                  Giờ công: <strong>{row.work_hours != null ? `${formatHoursVi(row.work_hours)}h` : "0h"}</strong>{" "}
                  &nbsp;Giờ OT: <strong>{row.ot_hours != null ? `${formatHoursVi(row.ot_hours)}h` : "0h"}</strong>
                </div>
              )}
            </div>
          </div>
          <div className={`${styles.modalFoot} ${styles.modalFootSpace}`}>
            {isProcessMode ? (
              <>
                {/* Đang sửa lại 1 record ĐÃ CHỐT (recorded/not_recorded) — "Hoãn
                    ghi nhận" ở đây vô nghĩa (record đã có kết quả, "hoãn" hàm ý
                    đẩy NGƯỢC về hàng chờ xử lý một quyết định CHƯA từng đưa ra).
                    Thay bằng "Hủy" — thoát khỏi phiên sửa, không đổi gì. Record
                    CÒN pending_review (chưa từng quyết định) giữ nguyên "Hoãn
                    ghi nhận" làm lối thoát trung tính đúng thiết kế. */}
                {/* #780: NV xin đóng ca tồn đọng (không GPS) — "Ghi nhận công" là duyệt
                    (ghi giờ ra), "Từ chối đóng ca" mở lại bản ghi. */}
                {modalRow?.close_requested && (
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnSecondary}`}
                    data-testid="reject-close-btn"
                    onClick={() => openConfirm("reject_close")}
                  >
                    Từ chối đóng ca
                  </button>
                )}
                {isEditingAlreadyResolvedRow ? (
                  <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={cancelEdit}>
                    Hủy
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnSecondary}`}
                    onClick={() => openConfirm("defer")}
                  >
                    Hoãn ghi nhận
                  </button>
                )}
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  disabled={exceptionsInvalid || (isEditingAlreadyResolvedRow && isRecordUnchangedFromSaved)}
                  title={
                    exceptionsInvalid
                      ? "Còn dòng ngoại lệ chưa hợp lệ — sửa trước khi ghi nhận."
                      : isEditingAlreadyResolvedRow && isRecordUnchangedFromSaved
                        ? 'Chưa đổi gì so với bản ghi đã lưu — bấm "Hủy" để thoát, hoặc sửa 1 trường bất kỳ trước khi ghi nhận lại.'
                        : undefined
                  }
                  onClick={handleRecordClick}
                >
                  Ghi nhận công
                </button>
              </>
            ) : (
              <>
                <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={closeModal}>
                  Đóng
                </button>
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setEditMode(true)}>
                  Sửa lại
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Render: popover chặn "Ghi nhận công" khi còn nhãn chưa quyết định ──
  // KHÁC dialog [19] (xác nhận lưu THẬT) — popover này đứng TRƯỚC, thuần hỏi
  // "Điều chỉnh" hay "Bỏ qua" cho từng nhãn; chọn xong hết mới tới [19].

  const renderPendingDecisionPopover = () => {
    if (!pendingDecisionOpen || !modalRow || undecidedLabels.length === 0) return null;
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="sc011PendingDecisionTitle" style={{ maxWidth: 440 }}>
          <div className={styles.modalHead}>
            <h2 className={styles.modalTitle} id="sc011PendingDecisionTitle">
              Xử lý nhãn lệch giờ trước khi ghi nhận
            </h2>
          </div>
          <div className={styles.modalBody}>
            <p style={{ marginTop: 0, fontSize: "var(--nqh-text-sm)", color: "var(--nqh-gray-700)" }}>
              Chọn "Điều chỉnh" (thêm dòng trừ/cộng giờ ở khối "Xử lý ngoại lệ giờ công") hoặc "Bỏ qua" (xác
              nhận không cần điều chỉnh) cho {undecidedLabels.length > 1 ? "từng nhãn" : "nhãn"} dưới đây:
            </p>
            <div className={styles.labelDecisions}>
              {undecidedLabels.map(({ code, labelVi }) => (
                <div key={code} className={styles.labelDecisionRow}>
                  <span>
                    Nhãn: <strong>{labelVi}</strong>
                  </span>
                  <div className={styles.labelDecisionActions}>
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                      onClick={() => decideLabel(code, labelVi, "adjust")}
                    >
                      Điều chỉnh
                    </button>
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                      onClick={() => decideLabel(code, labelVi, "ignore")}
                    >
                      Bỏ qua
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.modalFoot}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setPendingDecisionOpen(false)}>
              Hủy
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Render: dialog [19] ──

  const renderConfirmDialog = () => {
    if (!modalRow || !confirmAction) return null;
    const row = modalRow;
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="sc011ConfirmTitle">
          {/* 2026-09-07 (sync wireframe): ASCII dialog [19] (đăng ký dòng
              5030-5047) chỉ vẽ tiêu đề, KHÔNG có nút [✕] riêng — "Hủy" ở
              footer đã đủ, trùng chức năng với ✕ cũ (cả 2 cùng gọi
              setConfirmAction(null)). */}
          <div className={styles.modalHead}>
            <h2 className={styles.modalTitle} id="sc011ConfirmTitle">
              {confirmAction === "record"
                ? "Ghi nhận công?"
                : confirmAction === "reject_close"
                  ? "Từ chối yêu cầu đóng ca?"
                  : "Hoãn ghi nhận?"}
            </h2>
          </div>
          <div className={styles.modalBody}>
            {confirmAction === "record" ? (
              <>
                <div className={styles.attLines}>
                  <div>
                    {row.full_name} — record {formatTimeRange(row.clock_in, row.clock_out)}:
                  </div>
                  {computedInISO || computedOutISO ? (
                    <>
                      <div>· Vào được tính: {computedInISO ? formatTime(computedInISO) : "—"}</div>
                      <div>· Ra được tính: {computedOutISO ? formatTime(computedOutISO) : "—"}</div>
                    </>
                  ) : (
                    <div>Giờ công: 0h</div>
                  )}
                </div>
                {/* Dialog [19]: 2 dòng tổng TÁCH RIÊNG theo loại, mỗi dòng chỉ
                    hiện khi `[18e]` có ≥1 dòng đúng loại đó (registry [19]). */}
                {exceptionsDraft.some((e) => e.kind === "overtime") && (
                  <div className={styles.attLines}>
                    · Ngoài giờ ({exceptionsDraft.filter((e) => e.kind === "overtime").length} dòng):{" "}
                    {exceptionsDraft
                      .filter((e) => e.kind === "overtime")
                      .map((e) => `${e.from_time}–${e.to_time} (${formatHoursVi(exceptionSpanHours(e.from_time, e.to_time))}h)`)
                      .join(", ")}
                  </div>
                )}
                {exceptionsDraft.some((e) => e.kind === "adjustment") && (
                  <div className={styles.attLines}>
                    · Điều chỉnh ({exceptionsDraft.filter((e) => e.kind === "adjustment").length} dòng):{" "}
                    {exceptionsDraft
                      .filter((e) => e.kind === "adjustment")
                      .map((e) => `${(e.hours ?? 0) > 0 ? "+" : "−"} ${formatHoursVi(Math.abs(e.hours ?? 0))}h — ${e.note ?? ""}`)
                      .join(", ")}
                  </div>
                )}
                <div className={styles.attLines} style={{ marginTop: "var(--nqh-space-3)" }}>
                  <div style={{ fontWeight: "var(--nqh-weight-semibold)" }}>Kết quả sau khi ghi nhận:</div>
                  <div>· Giờ công: {formatHoursVi(previewWorkHours)}h</div>
                  <div>· Giờ OT: {formatHoursVi(previewOtHours)}h</div>
                </div>
                {/* ECO-DEC-2026-144: ghi nhận công tay PHẢI có lý do — server trả 422
                    nếu thiếu; cùng state với ô "Ghi chú quản lý" của modal [18]. */}
                <div className={styles.field} style={{ marginTop: "var(--nqh-space-3)" }}>
                  <label htmlFor="sc011ConfirmReason">Lý do ghi nhận công tay (bắt buộc)</label>
                  <textarea
                    id="sc011ConfirmReason"
                    className={styles.input}
                    rows={2}
                    required
                    value={managerNoteDraft}
                    onChange={(e) => setManagerNoteDraft(e.target.value)}
                  />
                </div>
                {overlapRow && (
                  <div className={styles.confirmWarn}>
                    <WarnIcon /> Cảnh báo (không chặn): record này CHỒNG thời gian với record khác cùng ngày ({formatTimeRange(overlapRow.clock_in, overlapRow.clock_out)}) — kiểm tra tránh cộng trùng giờ công.
                  </div>
                )}
              </>
            ) : confirmAction === "reject_close" ? (
              <>
                <div className={styles.attLines}>
                  <div>Ca của {row.full_name} sẽ mở lại, chưa tính giờ công. Quản lý tự ghi nhận giờ ra sau.</div>
                </div>
                <div className={styles.field} style={{ marginTop: "var(--nqh-space-3)" }}>
                  <label htmlFor="sc011RejectCloseReason">Lý do từ chối (bắt buộc)</label>
                  <textarea
                    id="sc011RejectCloseReason"
                    className={styles.input}
                    rows={2}
                    required
                    value={managerNoteDraft}
                    onChange={(e) => setManagerNoteDraft(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div className={styles.attLines}>
                <div>Sẽ xử lý lại vào kỳ lương sau, chưa ghi giờ công ngay.</div>
                {row.action_state === "recorded" || row.action_state === "not_recorded" ? (
                  <div>Nhãn Thao tác sẽ về lại "Xác nhận công", đẩy vào hàng chờ xử lý.</div>
                ) : null}
              </div>
            )}
          </div>
          <div className={styles.modalFoot}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setConfirmAction(null)}>
              Hủy
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={resolveMutation.isPending || (confirmAction !== "defer" && !managerNoteDraft.trim())}
              onClick={submitResolve}
            >
              {resolveMutation.isPending && <span className={styles.spinner} aria-hidden="true" />}
              Xác nhận
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Render: dialog xác nhận bulk (checkbox cột "Thao tác" + thanh bulk) ──
  // Component RIÊNG với `renderConfirmDialog()` (dùng `bulkConfirmAction`
  // thay vì `confirmAction`) dù cùng khung modal — tách state để không đụng
  // hành vi modal đơn [18]/[19] vốn đã có test bao phủ.
  const renderBulkConfirmDialog = () => {
    if (!bulkConfirmAction || selectedRows.length === 0) return null;
    const rows = selectedRows;
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="sc011BulkConfirmTitle">
          <div className={styles.modalHead}>
            <h2 className={styles.modalTitle} id="sc011BulkConfirmTitle">
              {bulkConfirmAction === "record" ? `Ghi nhận công ${rows.length} bản ghi` : `Hoãn ghi nhận ${rows.length} bản ghi`}
            </h2>
          </div>
          <div className={styles.modalBody}>
            {bulkConfirmAction === "record" ? (
              <>
                <div className={styles.attLines}>Ghi nhận theo đúng giờ hệ thống đã tự tính cho từng bản ghi:</div>
                <ul className={styles.cfList}>
                  {rows.map((r) => {
                    const hasComputed = !!(r.computed_check_in || r.computed_check_out);
                    const { workHours } = previewHoursFor(r.computed_check_in, r.computed_check_out, r.exceptions);
                    return (
                      <li key={r.attendance_ref}>
                        {r.full_name} — {r.status_labels_vi.join(" · ") || "—"} →{" "}
                        {hasComputed ? (
                          <>
                            {formatTimeRange(r.computed_check_in, r.computed_check_out)} · {formatHoursVi(workHours)}h
                          </>
                        ) : (
                          <strong>không ghi nhận · 0h</strong>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <div className={styles.field}>
                  <label htmlFor="sc011BulkReason">Lý do ghi nhận công tay (bắt buộc)</label>
                  <textarea
                    id="sc011BulkReason"
                    className={styles.input}
                    rows={2}
                    required
                    value={bulkReason}
                    onChange={(e) => setBulkReason(e.target.value)}
                  />
                </div>
                {rows.some((r) => !r.computed_check_in && !r.computed_check_out) && (
                  <div className={styles.confirmWarn}>
                    <WarnIcon /> {rows.filter((r) => !r.computed_check_in && !r.computed_check_out).length} bản ghi sẽ thành "Không ghi nhận ·
                    0h" vì hệ thống không xác định được giờ công. Muốn tự điền giờ thì mở từng bản ghi.
                  </div>
                )}
              </>
            ) : (
              <>
                <div className={styles.attLines}>Các bản ghi sau sẽ chuyển về "Xác nhận công" (chưa ghi giờ công):</div>
                <ul className={styles.cfList}>
                  {rows.map((r) => (
                    <li key={r.attendance_ref}>
                      {r.full_name} — {formatTimeRange(r.clock_in, r.clock_out)} · {r.status_labels_vi.join(" · ") || "—"}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
          <div className={styles.modalFoot}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setBulkConfirmAction(null)}>
              Hủy
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={bulkResolveMutation.isPending || (bulkConfirmAction === "record" && !bulkReason.trim())}
              onClick={submitBulkResolve}
            >
              {bulkResolveMutation.isPending && <span className={styles.spinner} aria-hidden="true" />}
              Xác nhận
            </button>
          </div>
        </div>
      </div>
    );
  };

  // [Tra cứu luật gán nhãn] — bản ghi Quản lý đang chọn (nếu còn trong
  // `flatRows` sau khi đổi bộ lọc/khoảng ngày thì vẫn tìm thấy tiếp, không
  // reset theo filter — chủ động để trống khi không tìm thấy thay vì âm thầm
  // chọn nhầm bản ghi khác).
  const ruleLookupRow = flatRows.find((r) => rowKeyOf(r) === ruleLookupKey) ?? null;
  const ruleLookupThresholds: AttendanceRuleThresholds = {
    check_in_tolerance_minutes: data?.check_in_tolerance_minutes ?? 0,
    check_out_early_tolerance_minutes: data?.check_out_early_tolerance_minutes ?? 0,
    missed_check_in_threshold_minutes: data?.missed_check_in_threshold_minutes ?? 0,
    missed_check_out_threshold_minutes: data?.missed_check_out_threshold_minutes ?? 0,
    gps_radius_meters: data?.gps_radius_meters ?? 0,
  };

  // ══════════════════════════════ RENDER ══════════════════════════════

  return (
    <>
      <div className={styles.pageHead}>
        <h1 className={styles.pageHeadTitle}>Chấm công</h1>
        <div className={styles.pageHeadActions}>
          {pendingCount > 0 && !isOwnerReadOnly && (
            <button type="button" className={`${styles.chip} ${pendingFilterOn ? styles.chipActive : ""}`} onClick={toggleChip}>
              <WarnIcon /> {pendingCountDisplay} bản ghi cần xử lý ▸
            </button>
          )}
          <div className={styles.viewToggle} role="group" aria-label="Chế độ hiển thị">
            <button
              type="button"
              className={viewMode === "grouped" ? styles.viewToggleActive : ""}
              onClick={() => setViewMode("grouped")}
              title="Gộp nhiều bản ghi cùng nhân viên/ngày vào 1 nhóm hiển thị."
            >
              Gộp
            </button>
            <button
              type="button"
              className={viewMode === "flat" ? styles.viewToggleActive : ""}
              onClick={() => setViewMode("flat")}
              title="Hiện mỗi bản ghi chấm công thành 1 dòng riêng, không gộp nhóm."
            >
              Phẳng
            </button>
          </div>
          <span className={styles.menuAnchor} onMouseDown={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              aria-haspopup="true"
              aria-expanded={exportMenuOpen}
              disabled={exporting || !isOnline}
              onClick={(e) => {
                e.stopPropagation();
                setExportMenuOpen((v) => !v);
              }}
            >
              {exporting ? "Đang xuất…" : "Xuất ▼"}
            </button>
            {exportMenuOpen && (
              <div className={`${styles.menu} ${styles.menuRight} ${styles.exportMenu}`} role="menu" aria-label="Xuất dữ liệu">
                <button type="button" className={styles.exportItem} role="menuitem" onClick={() => handleExport("all")}>
                  <span>Xuất toàn bộ</span>
                  <PortalTooltip text={EXPORT_ALL_TOOLTIP} className={styles.infoIco} tabIndex={0} ariaLabel={EXPORT_ALL_TOOLTIP}>
                    <InfoIcon />
                  </PortalTooltip>
                </button>
                <button type="button" className={styles.exportItem} role="menuitem" onClick={() => handleExport("recorded")}>
                  <span>Xuất "Đã ghi nhận"</span>
                  <PortalTooltip text={EXPORT_RECORDED_TOOLTIP} className={styles.infoIco} tabIndex={0} ariaLabel={EXPORT_RECORDED_TOOLTIP}>
                    <InfoIcon />
                  </PortalTooltip>
                </button>
              </div>
            )}
          </span>
          {/* [10]⚙ "Cấu hình cơ sở" — MỞ KHOÁ 2026-08-26. Trước đây luôn ẩn vì
              chưa có endpoint nào; nay đủ 3 mảnh (cấp quyền SC-004 `[9]` · đặt
              giá trị PUT /manager/branch-workforce-config · ĐỌC qua
              resolve_shift_match_thresholds khi tính công). Vẫn ẩn khi cơ sở
              đang xem CHƯA được cấp quyền nào — đúng "ẨN HẲN theo mặc định". */}
          {canSelfConfigure && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              aria-label="Cấu hình cơ sở"
              title="Cấu hình cơ sở"
              onClick={() => setShowBranchConfig(true)}
            >
              ⚙
            </button>
          )}
        </div>
      </div>

      {isOwnerReadOnly && (
        <div className={`${styles.banner} ${styles.bannerInfo}`} role="status" style={{ marginBottom: "var(--nqh-space-3)" }}>
          Bạn đang xem ở chế độ chỉ đọc.
        </div>
      )}

      {!isOnline && (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginBottom: "var(--nqh-space-3)" }}>
          Mất kết nối. Xuất dữ liệu và xử lý chấm công tạm khoá cho tới khi mạng trở lại.
        </div>
      )}

      {exportError && (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginBottom: "var(--nqh-space-3)" }}>
          {exportError}
        </div>
      )}

      <div className={styles.filterBar}>
        {/* [1b] Tìm nhân viên (multi-select) — ISS-MGR-22: nhãn nút hiện theo
            tập ĐÃ ÁP DỤNG (committed); checkbox trong panel phản ánh tập ĐANG
            CHỌN (pending). Mở panel → nạp pending = committed hiện tại (giữ
            trạng thái tick từ lần mở trước); đóng — bấm lại nút HOẶC click ra
            ngoài (effect "Đóng panel/menu" phía trên) — → commit pending →
            committed, đúng 1 lần gọi API dù tick bao nhiêu checkbox. */}
        {/* 2026-09-23 (@reviewer MAJOR #1): wrapper PHẢI onMouseDown stopPropagation
            — PortalMenu đóng panel qua document-level mousedown (PortalMenu.tsx:82-84);
            thiếu guard này thì bấm trigger khi panel ĐANG mở sẽ mousedown→đóng+commit TRƯỚC,
            rồi onClick thấy state đã đóng → MỞ LẠI (nhấp nháy, nút không bao giờ đóng được).
            Cùng guard mọi menuAnchor cũ trong file này đã có (vd dòng 2207/2848). */}
        <span className={styles.menuAnchor} onMouseDown={(e) => e.stopPropagation()}>
          <button
            type="button"
            className={styles.selectBtn}
            aria-haspopup="true"
            aria-expanded={employeePanelOpen}
            onClick={(e) => {
              e.stopPropagation();
              if (employeePanelOpen) {
                setEmployeeIdsCommitted(employeeIdsPending);
                setEmployeePanelOpen(false);
              } else {
                const rect = e.currentTarget.getBoundingClientRect();
                setEmployeeMenuAnchor({ top: rect.top, bottom: rect.bottom, right: window.innerWidth - rect.right });
                setEmployeeIdsPending(employeeIdsCommitted);
                setEmployeePanelOpen(true);
              }
            }}
          >
            {/* 2026-09-07 (sync prototype `#empPickBtn`): icon kính lúp đứng
                TRƯỚC nhãn — trước đây chỉ có nhãn + chevron. */}
            <SearchIcon />
            {employeeIdsCommitted.length === 0 ? "Tìm nhân viên…" : `${employeeIdsCommitted.length} nhân viên`}
            <ChevronDownIcon />
          </button>
          {employeePanelOpen && employeeMenuAnchor && (
            <PortalMenu
              anchor={employeeMenuAnchor}
              onClose={() => {
                setEmployeeIdsCommitted(employeeIdsPending);
                setEmployeePanelOpen(false);
              }}
              ariaLabel="Tìm nhân viên"
              minWidth={260}
              style={{ width: 260, padding: "var(--nqh-space-2)", display: "flex", flexDirection: "column", gap: "var(--nqh-space-2)" }}
            >
              <div onClick={(e) => e.stopPropagation()} style={{ display: "contents" }}>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Tìm theo tên…"
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  autoFocus
                />
                {/* 2026-09-07 (sync prototype `renderEmpList()`): dòng "Tất cả"
                    (chọn-tất-cả) ở ĐẦU danh sách + đường kẻ phân cách — trước
                    đây panel này thiếu cả 2, khác panel "Khu vực làm việc" bên
                    cạnh (đã có "Tất cả" từ trước, cũng thiếu đường kẻ). Checked
                    khi KHÔNG chọn nhân viên nào (rỗng = "Tất cả nhân viên"). */}
                <label className={styles.employeeItem}>
                  <input type="checkbox" checked={employeeIdsPending.length === 0} onChange={() => setEmployeeIdsPending([])} />
                  Tất cả
                </label>
                <hr className={styles.pickSep} />
                <div className={styles.employeeList}>
                  {filteredEmployeeOptions.map((it) => (
                    <label key={it.user_id} className={styles.employeeItem}>
                      <input type="checkbox" checked={employeeIdsPending.includes(it.user_id)} onChange={() => toggleEmployee(it.user_id)} />
                      {it.full_name}
                    </label>
                  ))}
                  {filteredEmployeeOptions.length === 0 && <div className={`${styles.muted} ${styles.txtSm}`}>Không tìm thấy.</div>}
                </div>
                {/* 2026-09-07 (sync prototype `pick-foot`): chân panel — đếm số
                    đã chọn (hoặc "Tất cả nhân viên" khi rỗng) + nút xoá NHANH
                    cả tick lẫn ô tìm kiếm, disabled khi không có gì để xoá
                    (`dirty` = có tick HOẶC có chữ đang gõ tìm kiếm). Trước đây
                    panel này không có chân — phải đóng rồi mở lại nút mới xoá
                    được từng tick một. */}
                <div className={styles.pickFoot}>
                  <span className={`${styles.muted} ${styles.txtSm}`}>
                    {employeeIdsPending.length > 0 ? `${employeeIdsPending.length} đã chọn` : "Tất cả nhân viên"}
                  </span>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                    disabled={employeeIdsPending.length === 0 && !employeeSearch}
                    onClick={() => {
                      setEmployeeIdsPending([]);
                      setEmployeeSearch("");
                    }}
                  >
                    Xoá lọc
                  </button>
                </div>
              </div>
            </PortalMenu>
          )}
        </span>

        {/* [1] Địa điểm — Quản lý 1 cơ sở → khoá cứng (registry), badge tĩnh */}
        <span className={styles.locationBadge}>
          <PinIcon />
          Địa điểm: {branchLabel || "—"}
        </span>

        {/* [2] Khu vực làm việc — ISS-MGR-22: đổi từ single-select (menuitem,
            đóng ngay khi chọn) sang checkbox multi-select + apply-on-close,
            CÙNG pattern với "Tìm nhân viên" ở trên (dùng lại class
            `.employeePanel`/`.employeeList`/`.employeeItem`). Nhãn nút theo
            committed; checkbox theo pending. Lọc thật sự = CLIENT-SIDE trên
            `flatRows` (Quyết định 2 — KHÔNG gửi `work_area_id` lên backend). */}
        {/* 2026-09-23 (@reviewer MAJOR #1): cùng guard onMouseDown stopPropagation
            như "Tìm nhân viên" ở trên — lý do chi tiết xem comment tại chỗ đó. */}
        <span className={styles.menuAnchor} onMouseDown={(e) => e.stopPropagation()}>
          <button
            type="button"
            className={styles.selectBtn}
            aria-haspopup="true"
            aria-expanded={areaMenuOpen}
            onClick={(e) => {
              e.stopPropagation();
              if (areaMenuOpen) {
                setWorkAreaIdsCommitted(workAreaIdsPending);
                setAreaMenuOpen(false);
              } else {
                const rect = e.currentTarget.getBoundingClientRect();
                setAreaMenuAnchorPos({ top: rect.top, bottom: rect.bottom, right: window.innerWidth - rect.right });
                setWorkAreaIdsPending(workAreaIdsCommitted);
                setAreaMenuOpen(true);
              }
            }}
          >
            Khu vực làm việc: {workAreaIdsCommitted.length === 0 ? "Tất cả" : `${workAreaIdsCommitted.length} đã chọn`}
            <ChevronDownIcon />
          </button>
          {areaMenuOpen && areaMenuAnchor && (
            <PortalMenu
              anchor={areaMenuAnchor}
              onClose={() => {
                setWorkAreaIdsCommitted(workAreaIdsPending);
                setAreaMenuOpen(false);
              }}
              ariaLabel="Lọc khu vực"
              minWidth={260}
              style={{ width: 260, padding: "var(--nqh-space-2)", display: "flex", flexDirection: "column", gap: "var(--nqh-space-2)" }}
            >
              <div onClick={(e) => e.stopPropagation()} style={{ display: "contents" }}>
                <label className={styles.employeeItem}>
                  <input type="checkbox" checked={workAreaIdsPending.length === 0} onChange={() => setWorkAreaIdsPending([])} />
                  Tất cả
                </label>
                <hr className={styles.pickSep} />
                <div className={styles.employeeList}>
                  {areaOptions.map((a) => (
                    <label key={a.id} className={styles.employeeItem}>
                      <input type="checkbox" checked={workAreaIdsPending.includes(a.id)} onChange={() => toggleWorkArea(a.id)} />
                      {a.name}
                    </label>
                  ))}
                </div>
                {/* 2026-09-07: đồng bộ chân panel với "Tìm nhân viên" cạnh bên
                    (cùng pattern checkbox multi-select apply-on-close, ISS-MGR-22) —
                    wireframe/prototype không có panel riêng cho khối này (chỉ
                    single-select `simpleMenu`) nên không có mẫu để đối chiếu
                    trực tiếp; thêm để nhất quán NỘI BỘ giữa 2 panel multi-select
                    dùng chung 1 bộ class trong cùng màn. */}
                <div className={styles.pickFoot}>
                  <span className={`${styles.muted} ${styles.txtSm}`}>
                    {workAreaIdsPending.length > 0 ? `${workAreaIdsPending.length} đã chọn` : "Tất cả khu vực"}
                  </span>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                    disabled={workAreaIdsPending.length === 0}
                    onClick={() => setWorkAreaIdsPending([])}
                  >
                    Xoá lọc
                  </button>
                </div>
              </div>
            </PortalMenu>
          )}
        </span>

        {/* [3] <thời gian> */}
        <ContinuousScrollDatePicker
          value={dateFilter}
          onApply={setDateFilter}
          triggerClassName={styles.selectBtn}
          ariaLabel="Chọn ngày hoặc khoảng ngày"
        />
        {/* [9] Hôm nay */}
        <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setDateFilter({ mode: "single", date: todayISO })}>
          Hôm nay
        </button>

        {/* BOT-050 (#839, 2026-09-14) — filter "Trạng thái", trước đây thiếu
            hoàn toàn trên UI dù backend đã hỗ trợ sẵn. Native <select>, cùng
            pattern với [Tra cứu luật gán nhãn] bên dưới (styles.selectBtn) —
            đơn-lựa-chọn, KHÔNG cần committed/pending như 2 panel multi-select
            ở trên.
            2026-09-19 (yêu cầu trực tiếp, sửa lệch): mũi tên MẶC ĐỊNH của
            trình duyệt cho `<select>` tự chừa khoảng đệm phải RIÊNG của nó,
            cộng dồn với padding-phải của `.selectBtn` → khoảng cách chữ↔mũi
            tên rộng bất thường, lệch hẳn so với các nút khác cạnh bên (những
            nút đó tự vẽ `<ChevronDownIcon/>` sát chữ, không dùng mũi tên có
            sẵn). Fix: `appearance:none` tắt mũi tên gốc + tự vẽ
            `ChevronDownIcon` đè lên bằng `.selectArrow` (absolute,
            pointer-events:none) — khớp đúng khoảng cách/kiểu icon với
            "Tìm nhân viên"/"Khu vực làm việc". */}
        <span className={styles.selectWrap}>
          <select
            className={`${styles.selectBtn} ${styles.selectNative}`}
            aria-label="Lọc theo trạng thái"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Tất cả trạng thái</option>
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className={styles.selectArrow} aria-hidden="true">
            <ChevronDownIcon />
          </span>
        </span>

        {/* [5] Đếm */}
        <span className={styles.attCount}>{data ? `${rowCountLabel} dòng` : ""}</span>
      </div>

      {listQuery.isLoading ? (
        <div aria-busy="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.skeletonRow}`} />
          ))}
        </div>
      ) : listQuery.isError ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          Không tải được chấm công · Thử lại
          <button type="button" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} style={{ marginLeft: "auto" }} onClick={() => listQuery.refetch()}>
            Thử lại
          </button>
        </div>
      ) : (
        <div
          style={!isOnline ? { pointerEvents: "none", opacity: 0.6 } : undefined}
          aria-disabled={!isOnline || undefined}
        >
          {selectedCount > 0 && (
            <div className={styles.bulkBar}>
              <span>{selectedCount} đã chọn</span>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`} onClick={() => openBulkConfirm("record")}>
                Ghi nhận công {selectedCount} bản ghi →
              </button>
              <button type="button" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} onClick={() => openBulkConfirm("defer")}>
                Hoãn ghi nhận {selectedCount} bản ghi →
              </button>
              <button type="button" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} onClick={() => setSelectedIds(new Set())}>
                Bỏ chọn
              </button>
            </div>
          )}
          {renderTable()}
          {renderCards()}
        </div>
      )}

      {/* [Tra cứu luật gán nhãn] — mới, KHÔNG có trong wireframe registry (tự
          nghiệp vụ hoá theo yêu cầu trực tiếp): giúp Quản lý chọn 1 bản ghi
          BẤT KỲ đang hiển thị và xem hệ thống đã áp dụng luật nào cho từng
          nhãn Trạng thái — độc lập với modal [18]/[18-RO], không cần mở modal
          mới hiểu được vì sao 1 nhãn xuất hiện. */}
      {!listQuery.isLoading && !listQuery.isError && (
        <div className={styles.ruleLookup}>
          <h2 className={styles.ruleLookupTitle}>Tra cứu luật gán nhãn</h2>
          <p className={`${styles.muted} ${styles.txtSm}`} style={{ marginTop: 0 }}>
            Chọn 1 bản ghi đang hiển thị ở trên để xem hệ thống đã dùng luật nào để gán từng nhãn "Trạng thái"
            cho bản ghi đó, và Quản lý cần hiểu/làm gì với nhãn này.
          </p>
          <select
            className={styles.selectBtn}
            aria-label="Chọn bản ghi để tra cứu luật gán nhãn"
            value={ruleLookupKey}
            onChange={(e) => setRuleLookupKey(e.target.value)}
          >
            <option value="">— Chọn bản ghi —</option>
            {flatRows.map((r) => (
              <option key={rowKeyOf(r)} value={rowKeyOf(r)}>
                {r.full_name} · {r.date} · {r.status_labels_vi.length > 0 ? r.status_labels_vi.join(", ") : "chưa có nhãn"}
              </option>
            ))}
          </select>

          {ruleLookupKey && !ruleLookupRow && (
            <p className={`${styles.muted} ${styles.txtSm}`}>Không tìm thấy bản ghi này trong danh sách đang hiển thị (có thể đã đổi bộ lọc).</p>
          )}

          {ruleLookupRow && (
            <div className={styles.ruleLookupResult}>
              <div className={styles.ruleLookupResultHead}>
                <strong>{ruleLookupRow.full_name}</strong> — {ruleLookupRow.date} — mã bản ghi:{" "}
                <code className={styles.ruleCode}>{rowKeyOf(ruleLookupRow)}</code>
              </div>
              {ruleLookupRow.status_labels.length === 0 ? (
                <p className={`${styles.muted} ${styles.txtSm}`}>Bản ghi này chưa có nhãn "Trạng thái" nào.</p>
              ) : (
                <ul className={styles.ruleLookupList}>
                  {ruleLookupRow.status_labels.map((code, i) => {
                    const ex = explainStatusLabel(code, ruleLookupRow.status_labels_vi[i] ?? code, ruleLookupThresholds);
                    return (
                      <li key={`${code}-${i}`} className={styles.ruleLookupItem}>
                        <div className={styles.ruleLookupItemHead}>
                          <code className={styles.ruleCode}>{ex.code}</code>
                          <strong>{ex.labelVi}</strong>
                        </div>
                        <p className={styles.ruleLookupRuleText}>{ex.rule}</p>
                        <p className={styles.ruleLookupManagerNote}>👉 {ex.managerNote}</p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {renderModal()}
      {showBranchConfig && branchCfg && (
        <BranchConfigModal
          initial={branchCfg}
          onClose={() => setShowBranchConfig(false)}
          onSaved={setBranchCfg}
        />
      )}
      {renderPendingDecisionPopover()}
      {renderConfirmDialog()}
      {renderBulkConfirmDialog()}

      {toast && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}
    </>
  );
};

export default ManagerAttendancePage;
